import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api, ApiError } from "../../api/client";
import { useTabParam } from "../../hooks/useTabParam";
import ColleagueFeedbackMatrix from "../../components/ColleagueFeedbackMatrix";
import CycleForm from "../../components/CycleForm";
import EmployeeSearch, { EmployeeSearchMulti } from "../../components/EmployeeSearch";
import ReviewStagePips from "../../components/ReviewStagePips";
import { useFeatures } from "../../context/FeaturesContext";
import type { Site, Department } from "../../types";
import { reviewStatusLabel, reviewStatusNext, reviewStatusTone } from "../../utils/reviewStatus";
import { selfRatingText } from "../../utils/appraisal";
import ResendColleagueButton from "../../components/appraisal/ResendColleagueButton";
import ObjectivesOverview from "../../components/appraisal/ObjectivesOverview";
import JobDescriptionsOverview from "../../components/jd/JobDescriptionsOverview";
import { ChevronDown, ChevronUp, FileText, Info } from "lucide-react";

interface ReviewCycle {
  id: number;
  name: string;
  review_period_start: string;
  review_period_end: string;
  schedule_type: string;
  schedule_month: number | null;
  schedule_day: number | null;
  scope_type: string;
  scope_id: number | null;
  deadline_self_days: number;
  deadline_peer_days: number;
  deadline_manager_days: number;
  status: string;
  total_reviews: number;
  completed_reviews: number;
  status_breakdown: Record<string, number>;
  status_counts?: { not_scheduled: number; active: number; meeting_pending: number; completed: number; scheduled: number; total: number };
  created_at: string;
  current_period_start?: string | null;
  current_period_end?: string | null;
  reviewed_in_period?: number;
  is_recurring?: boolean;
  period_start_month?: number | null;
  period_start_day?: number | null;
  is_archived?: boolean;
  archived_at?: string | null;
  colleague_feedback_timing?: string;
  template_id?: number | null;
  auto_assign_site_id?: number | null;
  auto_assign_department_id?: number | null;
  auto_assign_region_id?: number | null;
  specific_employee_ids?: number[] | null;
}

interface CycleReview {
  id: number | null;
  cycle_id?: number;
  employee_id: number;
  employee_name: string;
  manager_id?: number | null;
  manager_name: string | null;
  site_name?: string | null;
  department_name?: string | null;
  review_period_start?: string | null;
  review_period_end?: string | null;
  status: string;
  deadline: string | null;
  self_assessment_deadline?: string | null;
  colleague_feedback_deadline?: string | null;
  manager_review_deadline?: string | null;
  self_rating: number | null;
  overall_self_rating?: number | null;
  rating_words?: boolean;
  appraisal_year?: number | null;
  peer_rating: number | null;
  manager_rating: number | null;
  overall_manager_rating?: number | null;
  overall_agreed_rating?: number | null;
  is_historical?: boolean;
  historical_summary?: string | null;
  review_date?: string | null;
  document_count?: number;
  colleague_count?: number;
  scheduled_start_date?: string | null;
  individual_self_deadline?: string | null;
  individual_colleague_deadline?: string | null;
  individual_manager_deadline?: string | null;
  triggered_at?: string | null;
  auto_trigger_annually?: boolean;
  colleague_focus_areas?: string[] | null;
  eligible?: boolean;
  has_current_review?: boolean;
  current_review_status?: string | null;
  current_review_id?: number | null;
}

interface ColleagueFeedbackItem {
  id: number;
  review_id: number;
  colleague_id: number;
  colleague_name: string;
  focus_area: string;
  status: string;
  deadline: string | null;
  submitted_at: string | null;
  invited_at?: string | null;
}

interface PendingPeer {
  name: string;
  deadline: string | null;
  expired: boolean;
}

/** Per-review breakdown of peer feedback (admin/manager only — carries names). */
interface ColleagueStatus {
  submitted: { name: string }[];
  pending: PendingPeer[];
}

const ADMIN_PERF_TABS = ["360-reviews", "templates", "objectives", "job-descriptions"] as const;

interface ReviewTemplate360 {
  id: number;
  name: string;
  description: string | null;
  review_type: string;
  is_active: boolean;
  employee_question_count: number;
  colleague_question_count: number;
  manager_question_count: number;
}

export default function AdminPerformancePage() {
  const { isEnabled } = useFeatures();
  const [templates360, setTemplates360] = useState<ReviewTemplate360[]>([]);
  const [tab, setTab] = useTabParam(ADMIN_PERF_TABS, "360-reviews");
  const [showTemplate360Form, setShowTemplate360Form] = useState(false);
  const [editingTemplateId, setEditingTemplateId] = useState<number | null>(null);
  const [editingCycleId, setEditingCycleId] = useState<number | null>(null);

  // 360 Reviews state
  const [cycles, setCycles] = useState<ReviewCycle[]>([]);
  const [showCycleForm, setShowCycleForm] = useState(false);
  const [selectedCycleId, setSelectedCycleId] = useState<number | null>(null);
  const [allReviews, setAllReviews] = useState<CycleReview[]>([]);
  const [triggeringId, setTriggeringId] = useState<number | null>(null);
  const [previewCycleId, setPreviewCycleId] = useState<number | null>(null);
  const [previewEmployees, setPreviewEmployees] = useState<{ id: number; first_name: string; last_name: string; site_name: string }[]>([]);
  const [previewLoading, setPreviewLoading] = useState(false);

  // Status filter for 360 reviews — persisted in URL
  const [searchParams, setSearchParams] = useSearchParams();
  const statusFilter = (searchParams.get("filter") || "active") as "all" | "active" | "meeting" | "completed";
  const setStatusFilter = useCallback((f: "all" | "active" | "meeting" | "completed") => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      if (f === "active") next.delete("filter");
      else next.set("filter", f);
      return next;
    }, { replace: true });
  }, [setSearchParams]);

  // Review search/filters — URL-persisted
  const searchQuery = searchParams.get("q") || "";
  const filterSite = searchParams.get("site") || "";
  const filterDept = searchParams.get("dept") || "";
  const filterManager = searchParams.get("mgr") || "";
  const filterEmployee = searchParams.get("employee") || "";

  // Debounced search input
  const [searchInput, setSearchInput] = useState(searchQuery);
  const searchTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const handleSearchChange = useCallback((value: string) => {
    setSearchInput(value);
    if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
    searchTimerRef.current = setTimeout(() => {
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        if (value) next.set("q", value);
        else next.delete("q");
        return next;
      }, { replace: true });
    }, 200);
  }, [setSearchParams]);

  const setFilterParam = useCallback((key: string, value: string) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      if (value) next.set(key, value);
      else next.delete(key);
      if (key === "site") next.delete("dept");
      return next;
    }, { replace: true });
  }, [setSearchParams]);

  const effectiveSearch = searchQuery || filterEmployee;
  const hasActiveFilters = !!(effectiveSearch || filterSite || filterDept || filterManager);

  const clearFilters = useCallback(() => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.delete("q");
      next.delete("site");
      next.delete("dept");
      next.delete("mgr");
      next.delete("employee");
      return next;
    }, { replace: true });
    setSearchInput("");
  }, [setSearchParams]);

  const [allSites, setAllSites] = useState<Site[]>([]);
  const [allDepartments, setAllDepartments] = useState<Department[]>([]);

  useEffect(() => {
    void Promise.all([
      api.get<Site[]>("/api/sites").catch(() => []),
      api.get<Department[]>("/api/departments").catch(() => []),
    ]).then(([s, d]) => { setAllSites(s); setAllDepartments(d); });
  }, []);

  const filteredDepartments = filterSite
    ? allDepartments.filter((d) => d.site_id === Number(filterSite))
    : allDepartments;

  const uniqueManagers = Array.from(
    new Map(allReviews.filter((cr) => cr.manager_id && cr.manager_name).map((cr) => [cr.manager_id, cr.manager_name!])).entries()
  ).sort((a, b) => a[1].localeCompare(b[1]));

  const matchesFilters = useCallback((cr: CycleReview) => {
    if (effectiveSearch && !cr.employee_name.toLowerCase().includes(effectiveSearch.toLowerCase())) return false;
    if (filterSite && cr.site_name !== allSites.find((s) => s.id === Number(filterSite))?.name) return false;
    if (filterDept && cr.department_name !== allDepartments.find((d) => d.id === Number(filterDept))?.name) return false;
    if (filterManager && cr.manager_id !== Number(filterManager)) return false;
    return true;
  }, [effectiveSearch, filterSite, filterDept, filterManager, allSites, allDepartments]);

  const reviewsForCycle = useCallback((cycleId: number) => {
    return allReviews.filter((r) => r.cycle_id === cycleId);
  }, [allReviews]);

  // Historical review modal
  const [showHistoricalModal, setShowHistoricalModal] = useState(false);

  // Complete meeting modal
  const [completeMeetingReviewId, setCompleteMeetingReviewId] = useState<number | null>(null);

  // Individual reviews sub-tab within expanded cycle
  const [cycleSubTab, setCycleSubTab] = useState<"overview" | "individual">("overview");
  const [selectedReviewIds, setSelectedReviewIds] = useState<Set<number>>(new Set());
  const [bulkDatesModal, setBulkDatesModal] = useState<{ ids: number[]; cycleId: number } | null>(null);
  const [triggerConfirm, setTriggerConfirm] = useState<{ reviews: CycleReview[]; cycleId: number } | null>(null);
  const [individualTriggerConfirm, setIndividualTriggerConfirm] = useState<{ review: CycleReview; cycleId: number } | null>(null);

  // Toast
  const [toast, setToast] = useState<string | null>(null);
  const [showArchived, setShowArchived] = useState(false);
  // review_id -> peer submitted/pending breakdown (admin/manager only)
  const [colleagueStatus, setColleagueStatus] = useState<Record<string, ColleagueStatus>>({});

  const loadTemplates360 = useCallback(async () => {
    try {
      const data = await api.get<ReviewTemplate360[]>("/api/reviews/templates");
      setTemplates360(data);
    } catch { /* ignore */ }
  }, []);

  const loadCycles = useCallback(async () => {
    try {
      const [data, revs, colStatus] = await Promise.all([
        api.get<{ cycles: ReviewCycle[] }>(`/api/reviews/dashboard${showArchived ? "?include_archived=true" : ""}`),
        api.get<CycleReview[]>("/api/reviews?admin_context=true"),
        api.get<{ reviews: Record<string, ColleagueStatus> }>("/api/reviews/colleague-status"),
      ]);
      setCycles(data.cycles);
      setAllReviews(revs);
      setColleagueStatus(colStatus?.reviews ?? {});
    } catch (e) {
      if (e instanceof ApiError) {
        console.error("Failed to load review cycles:", e.message);
      }
    }
  }, [showArchived]);

  useEffect(() => { void loadCycles(); void loadTemplates360(); }, [loadCycles, loadTemplates360, showArchived]);

  // Auto-expand cycle when employee filter is set from review navigation
  const autoExpandedRef = useRef(false);
  useEffect(() => {
    if (autoExpandedRef.current || allReviews.length === 0) return;
    const employeeParam = searchParams.get("employee");
    if (!employeeParam) return;
    const matching = allReviews.find((r) => r.employee_name.toLowerCase().includes(employeeParam.toLowerCase()));
    if (matching?.cycle_id) {
      autoExpandedRef.current = true;
      setSelectedCycleId(matching.cycle_id);
    }
  }, [allReviews, searchParams]);

  const handleSelectCycle = (cycleId: number) => {
    setSelectedCycleId(selectedCycleId === cycleId ? null : cycleId);
  };

  const handleTriggerPreview = async (cycleId: number) => {
    setPreviewLoading(true);
    setPreviewCycleId(cycleId);
    try {
      const data = await api.post<{ total: number; scope_type: string; employees: { id: number; first_name: string; last_name: string; site_name: string }[] }>(`/api/reviews/cycles/${cycleId}/preview`, {});
      setPreviewEmployees(data.employees);
    } catch {
      setPreviewCycleId(null);
    }
    setPreviewLoading(false);
  };

  const confirmTrigger = async () => {
    if (!previewCycleId) return;
    setTriggeringId(previewCycleId);
    setPreviewCycleId(null);
    setPreviewEmployees([]);
    try {
      const res = await api.post<{ reviews_created: number; skipped: number; skipped_employees: string[] }>(
        `/api/reviews/cycles/${previewCycleId}/trigger`, {},
      );
      const msg = `Triggered ${res.reviews_created} reviews.` +
        (res.skipped > 0 ? ` Skipped ${res.skipped} (already have reviews).` : "");
      alert(msg);
      void loadCycles();
    } catch { /* ignore */ }
    setTriggeringId(null);
  };

  const handleResetCycle = async (cycleId: number) => {
    if (!confirm("Delete all not-started reviews from this cycle? Reviews already in progress will not be affected.")) return;
    try {
      const res = await api.delete<{ deleted: number; remaining: number }>(`/api/reviews/cycles/${cycleId}/reset`);
      alert(`Deleted ${res.deleted} reviews. ${res.remaining} remaining.`);
      void loadCycles();
    } catch { /* ignore */ }
  };

  const handleDeleteCycle = async (cycleId: number, cycleName: string) => {
    if (!confirm(`Delete cycle "${cycleName}"? This cannot be undone. Only draft cycles with no triggered reviews can be deleted.`)) return;
    try {
      await api.delete(`/api/reviews/cycles/${cycleId}`);
      void loadCycles();
      if (selectedCycleId === cycleId) setSelectedCycleId(null);
    } catch (err) {
      if (err instanceof ApiError) alert(err.message);
    }
  };

  const handleArchiveCycle = async (cycleId: number) => {
    try {
      await api.post(`/api/reviews/cycles/${cycleId}/archive`, {});
      void loadCycles();
    } catch (err) {
      if (err instanceof ApiError) alert(err.message);
    }
  };

  const handleUnarchiveCycle = async (cycleId: number) => {
    try {
      await api.post(`/api/reviews/cycles/${cycleId}/unarchive`, {});
      void loadCycles();
    } catch (err) {
      if (err instanceof ApiError) alert(err.message);
    }
  };

  const ACTIVE_STATUSES = ["not_started", "self_assessment_in_progress", "self_assessment_submitted", "colleague_feedback_in_progress", "colleague_feedback_complete", "manager_review_in_progress", "manager_review_submitted"];
  const MEETING_STATUSES = ["meeting_pending", "employee_response_pending"];
  const COMPLETED_STATUSES = ["completed"];

  const cycleHasStatusMatch = useCallback((cycleId: number, filter: string) => {
    const cyc = reviewsForCycle(cycleId);
    if (filter === "all") return true;
    if (filter === "active") return cyc.some((cr) => ACTIVE_STATUSES.includes(cr.status));
    if (filter === "meeting") return cyc.some((cr) => MEETING_STATUSES.includes(cr.status));
    if (filter === "completed") return cyc.some((cr) => COMPLETED_STATUSES.includes(cr.status) || cr.is_historical);
    return true;
  }, [reviewsForCycle]);

  const getFilteredReviewsForCycle = useCallback((cycleId: number) => {
    const cyc = reviewsForCycle(cycleId);
    let result = statusFilter === "all" ? cyc
      : statusFilter === "active" ? cyc.filter((cr) => ACTIVE_STATUSES.includes(cr.status))
      : statusFilter === "meeting" ? cyc.filter((cr) => MEETING_STATUSES.includes(cr.status))
      : cyc.filter((cr) => COMPLETED_STATUSES.includes(cr.status) || cr.is_historical);
    if (hasActiveFilters) result = result.filter(matchesFilters);
    return result;
  }, [reviewsForCycle, statusFilter, hasActiveFilters, matchesFilters]);

  const filteredCycles = cycles.filter((cycle) => {
    const statusMatch = cycleHasStatusMatch(cycle.id, statusFilter);
    if (!statusMatch) return false;
    if (!hasActiveFilters) return true;
    return getFilteredReviewsForCycle(cycle.id).length > 0;
  });

  const getStatusCounts = useCallback((cycleId: number) => {
    const cyc = reviewsForCycle(cycleId);
    const scheduled = cyc.filter((cr) => cr.scheduled_start_date && !cr.triggered_at).length;
    const active = cyc.filter((cr) => ACTIVE_STATUSES.includes(cr.status) && cr.triggered_at).length;
    const meeting = cyc.filter((cr) => MEETING_STATUSES.includes(cr.status)).length;
    const completed = cyc.filter((cr) => COMPLETED_STATUSES.includes(cr.status) || cr.is_historical).length;
    const notScheduled = cyc.filter((cr) => !cr.scheduled_start_date && !cr.triggered_at && cr.status === "not_started").length;
    return { not_scheduled: notScheduled, scheduled, active, meeting_pending: meeting, completed, total: cyc.length };
  }, [reviewsForCycle]);


  const statusBadge = (status: string) => reviewStatusTone(status);

  const handleDeleteTemplate360 = async (templateId: number, templateName: string) => {
    if (!confirm(`Delete template "${templateName}"? This cannot be undone.`)) return;
    try {
      await api.delete(`/api/reviews/templates/${templateId}`);
      void loadTemplates360();
    } catch (err) {
      if (err instanceof ApiError) alert(err.message);
    }
  };

  const handleDuplicateTemplate360 = async (templateId: number) => {
    try {
      await api.post(`/api/reviews/templates/${templateId}/duplicate`, {});
      void loadTemplates360();
    } catch (err) {
      if (err instanceof ApiError) alert(err.message);
    }
  };

  const handleCancelReview = async (reviewId: number, employeeName: string) => {
    if (!confirm(`Cancel this review for ${employeeName}? This will cancel all pending feedback requests.`)) return;
    try {
      await api.post(`/api/reviews/${reviewId}/cancel`, {});
      void loadCycles();
    } catch (err) {
      if (err instanceof ApiError) alert(err.message);
    }
  };

  const handleDeleteReview = async (reviewId: number) => {
    if (!confirm("Delete this review? This cannot be undone.")) return;
    try {
      await api.delete(`/api/reviews/${reviewId}`);
      void loadCycles();
    } catch (err) {
      if (err instanceof ApiError) alert(err.message);
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-semibold text-gray-900">Performance</h1>
        <div className="flex gap-2">
          {tab === "360-reviews" && (
            <>
              <button type="button" onClick={() => setShowHistoricalModal(true)} className="rounded-lg bg-indigo-600 px-4 py-2 text-sm font-semibold text-white hover:bg-indigo-700">Log Historical Review</button>
              <button type="button" onClick={() => setShowCycleForm(true)} className="rounded-lg bg-pme-blue px-4 py-2 text-sm font-semibold text-white">+ Create Review Cycle</button>
            </>
          )}
          {tab === "templates" && !editingTemplateId && <button type="button" onClick={() => setShowTemplate360Form(true)} className="rounded-lg bg-pme-blue px-4 py-2 text-sm font-semibold text-white">+ Create Template</button>}
        </div>
      </div>
      <nav className="flex gap-1 overflow-x-auto border-b border-gray-200">
        <TabBtn active={tab === "360-reviews"} onClick={() => setTab("360-reviews")}>Appraisals</TabBtn>
        <TabBtn active={tab === "templates"} onClick={() => setTab("templates")}>Templates</TabBtn>
        {isEnabled("goals_tracking_enabled") && (
          <TabBtn active={tab === "objectives"} onClick={() => setTab("objectives")}>Objectives</TabBtn>
        )}
        <TabBtn active={tab === "job-descriptions"} onClick={() => setTab("job-descriptions")}>Job descriptions</TabBtn>
      </nav>

      {showCycleForm && tab === "360-reviews" && (
        <CycleForm
          onSaved={() => { setShowCycleForm(false); void loadCycles(); }}
          onCancel={() => setShowCycleForm(false)}
        />
      )}
      {showTemplate360Form && (
        <TemplateMetaForm
          onSaved={() => { setShowTemplate360Form(false); void loadTemplates360(); }}
          onCancel={() => setShowTemplate360Form(false)}
        />
      )}

      {tab === "360-reviews" && (
        <div className="space-y-3">
          <HowReviewsWork />
          <NeedsAttentionStrip reviews={allReviews} colleagueStatus={colleagueStatus} onFilter={setStatusFilter} />
          {/* Status filter pills */}
          <div className="flex gap-2">
            {(["all", "active", "meeting", "completed"] as const).map((f) => (
              <button
                key={f}
                type="button"
                onClick={() => setStatusFilter(f)}
                className={`rounded-full px-3 py-1 text-xs font-semibold transition-colors ${statusFilter === f ? "bg-pme-blue text-white" : "bg-gray-100 text-gray-600 hover:bg-gray-200"}`}
              >
                {f === "all" ? "All" : f === "active" ? "Active" : f === "meeting" ? "Meeting Pending" : "Completed"}
              </button>
            ))}
            <label className="ml-auto flex items-center gap-1.5 text-xs text-gray-500 cursor-pointer">
              <input type="checkbox" checked={showArchived} onChange={(e) => setShowArchived(e.target.checked)} className="h-3.5 w-3.5 rounded border-gray-300" />
              Show archived
            </label>
          </div>
          {/* Search and filters */}
          <div className="flex flex-wrap items-center gap-2">
            <input
              type="text"
              value={filterEmployee || searchInput}
              onChange={(e) => handleSearchChange(e.target.value)}
              placeholder="Search employee name…"
              className="rounded-lg border border-gray-300 px-3 py-1.5 text-xs w-48"
            />
            <select
              value={filterSite}
              onChange={(e) => setFilterParam("site", e.target.value)}
              className="rounded-lg border border-gray-300 px-2 py-1.5 text-xs"
            >
              <option value="">All sites</option>
              {allSites.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
            </select>
            <select
              value={filterDept}
              onChange={(e) => setFilterParam("dept", e.target.value)}
              className="rounded-lg border border-gray-300 px-2 py-1.5 text-xs"
            >
              <option value="">All departments</option>
              {filteredDepartments.map((d) => <option key={d.id} value={d.id}>{d.name}{d.site_name ? ` (${d.site_name})` : ""}</option>)}
            </select>
            {uniqueManagers.length > 0 && (
              <select
                value={filterManager}
                onChange={(e) => setFilterParam("mgr", e.target.value)}
                className="rounded-lg border border-gray-300 px-2 py-1.5 text-xs"
              >
                <option value="">All managers</option>
                {uniqueManagers.map(([id, name]) => <option key={id} value={String(id)}>{name}</option>)}
              </select>
            )}
            {hasActiveFilters && (
              <button type="button" onClick={clearFilters} className="text-xs text-pme-blue hover:underline">
                Clear filters
              </button>
            )}
          </div>
          {cycles.length === 0 && !showCycleForm && <p className="text-sm text-gray-400">No review cycles created yet.</p>}
          {cycles.length > 0 && filteredCycles.length === 0 && <p className="text-sm text-gray-400">No cycles with matching reviews.</p>}
          {filteredCycles.map((cycle) => {
            const isSelected = selectedCycleId === cycle.id;
            const counts = getStatusCounts(cycle.id);
            const total = counts.total || 1;

            if (editingCycleId === cycle.id) {
              return (
                <div key={cycle.id} className="rounded-xl border border-gray-200 bg-white shadow-sm">
                  <CycleForm
                    editCycle={{
                      id: cycle.id,
                      name: cycle.name,
                      review_period_start: cycle.review_period_start,
                      review_period_end: cycle.review_period_end,
                      schedule_type: cycle.schedule_type,
                      schedule_month: cycle.schedule_month,
                      schedule_day: cycle.schedule_day,
                      scope_type: cycle.scope_type,
                      scope_id: cycle.scope_id,
                      auto_assign_site_id: cycle.auto_assign_site_id ?? null,
                      auto_assign_department_id: cycle.auto_assign_department_id ?? null,
                      auto_assign_region_id: cycle.auto_assign_region_id ?? null,
                      specific_employee_ids: cycle.specific_employee_ids ?? null,
                      deadline_self_days: cycle.deadline_self_days,
                      deadline_peer_days: cycle.deadline_peer_days,
                      deadline_manager_days: cycle.deadline_manager_days,
                      period_start_month: cycle.period_start_month ?? null,
                      period_start_day: cycle.period_start_day ?? null,
                      colleague_feedback_timing: cycle.colleague_feedback_timing,
                      template_id: cycle.template_id ?? null,
                    }}
                    onSaved={() => { setEditingCycleId(null); void loadCycles(); }}
                    onCancel={() => setEditingCycleId(null)}
                  />
                </div>
              );
            }

            return (
              <div key={cycle.id} className={`rounded-xl border shadow-sm ${cycle.is_archived ? "border-gray-300 bg-gray-50 opacity-75" : "border-gray-200 bg-white"}`}>
                <div
                  className="cursor-pointer p-4"
                  onClick={() => { handleSelectCycle(cycle.id); setCycleSubTab("overview"); setSelectedReviewIds(new Set()); }}
                >
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-semibold text-gray-900">{cycle.name}</p>
                      <p className="text-xs text-gray-500">
                        {cycle.review_period_start} &rarr; {cycle.review_period_end}
                        {" · "}
                        {cycle.schedule_type === "manual" ? "Manual" : cycle.schedule_type === "annual" ? "Annual" : "Semi-Annual"}
                        {" · "}
                        {!cycle.scope_type || cycle.scope_type === "all" || cycle.scope_type === "all_employees" ? "All Employees" : cycle.scope_type.charAt(0).toUpperCase() + cycle.scope_type.slice(1).replace(/_/g, " ")}
                      </p>
                      {cycle.is_recurring && cycle.current_period_start && (
                        <p className="text-xs text-indigo-600 mt-0.5">
                          Current period: {cycle.current_period_start} → {cycle.current_period_end}
                          {" · "}Reviewed: {cycle.reviewed_in_period ?? 0} of {cycle.total_reviews}
                        </p>
                      )}
                      {cycle.is_archived && (
                        <p className="text-[11px] text-gray-400 mt-0.5">Archived{cycle.archived_at ? ` on ${cycle.archived_at.slice(0, 10)}` : ""}</p>
                      )}
                    </div>
                    <div className="flex items-center gap-2">
                      {!cycle.is_archived && (
                        <button
                          type="button"
                          onClick={(e) => { e.stopPropagation(); setEditingCycleId(cycle.id); }}
                          className="rounded border border-gray-300 px-2 py-0.5 text-[10px] font-medium text-gray-700 hover:bg-gray-50"
                        >
                          Edit
                        </button>
                      )}
                      <span className={`rounded-full px-2 py-0.5 text-[10px] font-semibold ${cycle.status === "active" ? "bg-blue-50 text-blue-700" : cycle.status === "completed" ? "bg-green-50 text-green-700" : "bg-gray-100 text-gray-600"}`}>
                        {cycle.status.replace(/_/g, " ")}
                      </span>
                    </div>
                  </div>
                  {/* Status breakdown */}
                  <div className="mt-2 flex flex-wrap items-center gap-1.5 text-[11px]">
                    {counts.not_scheduled > 0 && (
                      <span className="rounded-full bg-gray-100 px-2 py-0.5 text-gray-600">{counts.not_scheduled} not scheduled</span>
                    )}
                    {counts.scheduled > 0 && (
                      <span className="rounded-full bg-purple-50 px-2 py-0.5 text-purple-700">{counts.scheduled} scheduled</span>
                    )}
                    <span className={`rounded-full bg-blue-50 px-2 py-0.5 text-blue-700 ${statusFilter === "active" ? "ring-1 ring-blue-300" : ""}`}>{counts.active} in progress</span>
                    <span className={`rounded-full bg-amber-50 px-2 py-0.5 text-amber-800 ${statusFilter === "meeting" ? "ring-1 ring-amber-300" : ""}`}>{counts.meeting_pending} at meeting stage</span>
                    <span className={`rounded-full bg-green-50 px-2 py-0.5 text-green-700 ${statusFilter === "completed" ? "ring-1 ring-green-300" : ""}`}>{counts.completed} completed</span>
                    <span className="text-gray-400">of {counts.total}</span>
                  </div>
                  {/* Multi-segment progress bar */}
                  <div className="mt-2 h-2 w-full rounded-full bg-gray-100 overflow-hidden flex">
                    {counts.completed > 0 && <div className="h-full bg-green-500" style={{ width: `${(counts.completed / total) * 100}%` }} />}
                    {counts.meeting_pending > 0 && <div className="h-full bg-amber-400" style={{ width: `${(counts.meeting_pending / total) * 100}%` }} />}
                    {counts.active > 0 && <div className="h-full bg-blue-500" style={{ width: `${(counts.active / total) * 100}%` }} />}
                    {counts.scheduled > 0 && <div className="h-full bg-purple-400" style={{ width: `${(counts.scheduled / total) * 100}%` }} />}
                  </div>
                </div>

                {isSelected && (
                  <div className="border-t border-gray-100">
                    {/* Sub-tabs */}
                    <div className="flex gap-1 px-4 pt-3">
                      <button type="button" onClick={() => setCycleSubTab("overview")} className={`rounded-t px-3 py-1.5 text-xs font-semibold ${cycleSubTab === "overview" ? "bg-pme-blue text-white" : "bg-gray-100 text-gray-600 hover:bg-gray-200"}`}>Overview</button>
                      <button type="button" onClick={() => setCycleSubTab("individual")} className={`rounded-t px-3 py-1.5 text-xs font-semibold ${cycleSubTab === "individual" ? "bg-pme-blue text-white" : "bg-gray-100 text-gray-600 hover:bg-gray-200"}`}>Individual Reviews</button>
                    </div>

                    {cycleSubTab === "overview" && (
                      <div className="px-4 pb-4 pt-3">
                        <div className="flex gap-2 mb-3">
                          {!cycle.is_archived && (
                            <>
                              <button type="button" onClick={(e) => { e.stopPropagation(); void handleTriggerPreview(cycle.id); }} disabled={triggeringId === cycle.id} className="rounded border border-gray-300 px-2 py-1 text-xs font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50">
                                {triggeringId === cycle.id ? "Triggering..." : "Trigger All Now"}
                              </button>
                              {cycle.total_reviews > 0 && (
                                <button type="button" onClick={(e) => { e.stopPropagation(); void handleResetCycle(cycle.id); }} className="rounded border border-red-200 px-2 py-1 text-xs font-medium text-red-600 hover:bg-red-50">Reset</button>
                              )}
                            </>
                          )}
                          {!cycle.is_archived ? (
                            <button type="button" onClick={(e) => { e.stopPropagation(); void handleArchiveCycle(cycle.id); }} className="rounded border border-gray-300 px-2 py-1 text-xs font-medium text-gray-500 hover:bg-gray-50">Archive</button>
                          ) : (
                            <button type="button" onClick={(e) => { e.stopPropagation(); void handleUnarchiveCycle(cycle.id); }} className="rounded border border-indigo-300 px-2 py-1 text-xs font-medium text-indigo-700 hover:bg-indigo-50">Unarchive</button>
                          )}
                          {!cycle.is_archived && cycle.total_reviews === 0 && (
                            <button type="button" onClick={(e) => { e.stopPropagation(); void handleDeleteCycle(cycle.id, cycle.name); }} className="rounded border border-red-200 px-2 py-1 text-xs font-medium text-red-600 hover:bg-red-50">Delete Cycle</button>
                          )}
                        </div>
                        <>
                          {(() => {
                              const filtered = getFilteredReviewsForCycle(cycle.id);
                              const highlightEmployee = searchParams.get("employee") || "";
                              return filtered.length === 0
                                ? <p className="text-sm text-gray-400">No reviews found.</p>
                                : (
                                  <div className="space-y-2">
                                    {filtered.map((cr) => (
                                      <CycleReviewCard
                                        key={cr.id}
                                        cr={cr}
                                        statusBadge={statusBadge}
                                        onCancel={handleCancelReview}
                                        onDelete={handleDeleteReview}
                                        onCompleteMeeting={setCompleteMeetingReviewId}
                                        colleagueStatus={cr.id != null ? colleagueStatus[String(cr.id)] : undefined}
                                        highlighted={!!highlightEmployee && cr.employee_name.toLowerCase().includes(highlightEmployee.toLowerCase())}
                                      />
                                    ))}
                                  </div>
                                );
                            })()}
                          </>
                      </div>
                    )}

                    {cycleSubTab === "individual" && (
                      <IndividualReviewsTab
                        cycle={cycle}
                        selectedIds={selectedReviewIds}
                        setSelectedIds={setSelectedReviewIds}
                        onTriggerIndividual={(cr) => setIndividualTriggerConfirm({ review: cr, cycleId: cycle.id })}
                        onTriggerBulk={(crs) => setTriggerConfirm({ reviews: crs, cycleId: cycle.id })}
                        onBulkDates={(ids) => setBulkDatesModal({ ids, cycleId: cycle.id })}
                        onCompleteMeeting={setCompleteMeetingReviewId}
                        onRefresh={loadCycles}
                        statusBadge={statusBadge}
                        colleagueStatus={colleagueStatus}
                      />
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {tab === "templates" && (
        editingTemplateId !== null ? (
          <TemplateEditor
            templateId={editingTemplateId}
            onClose={() => setEditingTemplateId(null)}
            onSaved={() => { setEditingTemplateId(null); void loadTemplates360(); }}
          />
        ) : (
          <div className="space-y-2">
            {templates360.map((t) => (
              <div key={t.id} className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <p className="text-sm font-semibold text-gray-900">{t.name}</p>
                      {!t.is_active && (
                        <span className="rounded-full bg-gray-100 px-2 py-0.5 text-[10px] font-semibold text-gray-500">Inactive</span>
                      )}
                    </div>
                    {t.description && <p className="text-xs text-gray-500 mt-0.5">{t.description}</p>}
                    <p className="text-xs text-gray-400 mt-1">
                      {t.review_type.replace(/_/g, " ")}
                      {" · "}
                      {t.employee_question_count} employee · {t.colleague_question_count} colleague · {t.manager_question_count} manager sections
                    </p>
                  </div>
                  <div className="flex gap-1 shrink-0">
                    <button
                      type="button"
                      onClick={() => setEditingTemplateId(t.id)}
                      className="rounded border border-gray-300 px-2 py-1 text-xs font-medium text-gray-700 hover:bg-gray-50"
                    >
                      Edit
                    </button>
                    <button
                      type="button"
                      onClick={() => void handleDuplicateTemplate360(t.id)}
                      className="rounded border border-indigo-300 px-2 py-1 text-xs font-medium text-indigo-700 hover:bg-indigo-50"
                    >
                      Duplicate
                    </button>
                    <button
                      type="button"
                      onClick={() => void handleDeleteTemplate360(t.id, t.name)}
                      className="rounded border border-red-200 px-2 py-1 text-xs font-medium text-red-600 hover:bg-red-50"
                    >
                      Delete
                    </button>
                  </div>
                </div>
              </div>
            ))}
            {templates360.length === 0 && !showTemplate360Form && <p className="text-sm text-gray-400">No review templates. Create one to get started.</p>}
          </div>
        )
      )}

      {tab === "objectives" && <ObjectivesOverview />}
      {tab === "job-descriptions" && <JobDescriptionsOverview />}

      {previewCycleId !== null && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" onClick={() => { setPreviewCycleId(null); setPreviewEmployees([]); }}>
          <div className="mx-4 w-full max-w-lg rounded-xl bg-white p-6 shadow-xl" onClick={(e) => e.stopPropagation()}>
            <h3 className="text-sm font-semibold text-gray-900 mb-3">Trigger Review Cycle</h3>
            {previewLoading ? (
              <p className="text-sm text-gray-400">Loading employees...</p>
            ) : (
              <>
                <p className="text-sm text-gray-600 mb-2">This will start appraisals for the following employees:</p>
                <div className="max-h-60 overflow-y-auto rounded-lg border border-gray-200 bg-gray-50">
                  {previewEmployees.map((emp) => (
                    <div key={emp.id} className="flex items-center justify-between border-b border-gray-100 px-3 py-2 last:border-0">
                      <span className="text-sm text-gray-800">{emp.first_name} {emp.last_name}</span>
                      {emp.site_name && <span className="text-xs text-gray-400">{emp.site_name}</span>}
                    </div>
                  ))}
                  {previewEmployees.length === 0 && (
                    <p className="px-3 py-4 text-center text-sm text-gray-400">No employees match the cycle scope.</p>
                  )}
                </div>
                <p className="mt-2 text-xs font-semibold text-gray-600">Total: {previewEmployees.length} employee{previewEmployees.length !== 1 ? "s" : ""}</p>
                <div className="mt-4 flex gap-2 justify-end">
                  <button type="button" onClick={() => { setPreviewCycleId(null); setPreviewEmployees([]); }} className="rounded-lg border border-gray-300 px-4 py-2 text-sm">Cancel</button>
                  <button
                    type="button"
                    onClick={() => void confirmTrigger()}
                    disabled={previewEmployees.length === 0}
                    className="rounded-lg bg-green-600 px-4 py-2 text-sm font-semibold text-white hover:bg-green-700 disabled:opacity-50"
                  >
                    Create Reviews
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* Historical Review Modal */}
      {showHistoricalModal && (
        <HistoricalReviewModal
          onClose={() => setShowHistoricalModal(false)}
          onSaved={() => {
            setShowHistoricalModal(false);
            void loadCycles();
            setToast("Historical review saved successfully");
            setTimeout(() => setToast(null), 3000);
          }}
        />
      )}

      {/* Complete Meeting Modal */}
      {completeMeetingReviewId !== null && (
        <CompleteMeetingModal
          reviewId={completeMeetingReviewId}
          onClose={() => setCompleteMeetingReviewId(null)}
          onCompleted={() => {
            setCompleteMeetingReviewId(null);
            void loadCycles();
            setToast("Review marked as completed");
            setTimeout(() => setToast(null), 3000);
          }}
        />
      )}

      {/* Individual Trigger Confirmation */}
      {individualTriggerConfirm && (
        <IndividualTriggerModal
          review={individualTriggerConfirm.review}
          cycleId={individualTriggerConfirm.cycleId}
          onClose={() => setIndividualTriggerConfirm(null)}
          onTriggered={() => {
            setIndividualTriggerConfirm(null);
            void loadCycles();
            setToast("Review triggered successfully");
            setTimeout(() => setToast(null), 3000);
          }}
        />
      )}

      {/* Bulk Trigger Confirmation */}
      {triggerConfirm && (
        <BulkTriggerModal
          reviews={triggerConfirm.reviews}
          cycleId={triggerConfirm.cycleId}
          onClose={() => setTriggerConfirm(null)}
          onTriggered={() => {
            setTriggerConfirm(null);
            setSelectedReviewIds(new Set());
            void loadCycles();
            setToast("Reviews triggered successfully");
            setTimeout(() => setToast(null), 3000);
          }}
        />
      )}

      {/* Bulk Dates Modal */}
      {bulkDatesModal && (
        <BulkDatesModal
          reviewIds={bulkDatesModal.ids}
          onClose={() => setBulkDatesModal(null)}
          onSaved={() => {
            setBulkDatesModal(null);
            setSelectedReviewIds(new Set());
            void loadCycles();
            setToast("Dates updated successfully");
            setTimeout(() => setToast(null), 3000);
          }}
        />
      )}

      {/* Toast */}
      {toast && (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-50 rounded-lg bg-green-600 px-5 py-2.5 text-sm font-semibold text-white shadow-lg">
          {toast}
        </div>
      )}
    </div>
  );
}

/* ---------- Historical Review Modal ---------- */

function HistoricalReviewModal({ onClose, onSaved }: { onClose: () => void; onSaved: () => void }) {
  const [employeeId, setEmployeeId] = useState<number | null>(null);
  const [employeeName, setEmployeeName] = useState("");
  const [reviewDate, setReviewDate] = useState("");
  const [periodStart, setPeriodStart] = useState("");
  const [periodEnd, setPeriodEnd] = useState("");
  const [reviewerId, setReviewerId] = useState<number | null>(null);
  const [reviewerName, setReviewerName] = useState("");
  const [overallRating, setOverallRating] = useState(3);
  const [summary, setSummary] = useState("");
  const [objectives, setObjectives] = useState<{ objective: string; due_date: string; status: string; category: string }[]>([]);
  const [actions, setActions] = useState<{ action_type: string; custom_label: string; assigned_to_id: number | null; assigned_to_name: string; due_date: string; status: string; notes: string }[]>([]);
  const [actionTriggers, setActionTriggers] = useState<{ action_code: string; title: string }[]>([]);
  const [files, setFiles] = useState<File[]>([]);
  const [dragOver, setDragOver] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    void (async () => {
      try {
        const triggers = await api.get<{ action_code: string; title: string }[]>("/api/settings/action-triggers");
        setActionTriggers(triggers);
      } catch { /* ignore */ }
    })();
  }, []);

  // Employee search
  const [empSearch, setEmpSearch] = useState("");
  const [empResults, setEmpResults] = useState<{ id: number; first_name: string; last_name: string; site_name?: string }[]>([]);
  const [showEmpResults, setShowEmpResults] = useState(false);
  const empTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Reviewer search
  const [revSearch, setRevSearch] = useState("");
  const [revResults, setRevResults] = useState<{ id: number; first_name: string; last_name: string; site_name?: string }[]>([]);
  const [showRevResults, setShowRevResults] = useState(false);
  const revTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const searchEmployees = (query: string, setter: typeof setEmpResults, showSetter: typeof setShowEmpResults) => {
    if (query.length < 2) { setter([]); showSetter(false); return; }
    void (async () => {
      try {
        const data = await api.get<{ id: number; first_name: string; last_name: string; site_name?: string }[]>(`/api/employees?search=${encodeURIComponent(query)}&is_active=true`);
        setter(data.slice(0, 10));
        showSetter(true);
      } catch { /* ignore */ }
    })();
  };

  const handleEmpSearchChange = (value: string) => {
    setEmpSearch(value);
    if (empTimerRef.current) clearTimeout(empTimerRef.current);
    empTimerRef.current = setTimeout(() => searchEmployees(value, setEmpResults, setShowEmpResults), 200);
  };

  const handleRevSearchChange = (value: string) => {
    setRevSearch(value);
    if (revTimerRef.current) clearTimeout(revTimerRef.current);
    revTimerRef.current = setTimeout(() => searchEmployees(value, setRevResults, setShowRevResults), 200);
  };

  const selectEmployee = (emp: { id: number; first_name: string; last_name: string; site_name?: string }) => {
    setEmployeeId(emp.id);
    setEmployeeName(`${emp.first_name} ${emp.last_name}${emp.site_name ? ` (${emp.site_name})` : ""}`);
    setEmpSearch(`${emp.first_name} ${emp.last_name}`);
    setShowEmpResults(false);
  };

  const selectReviewer = (emp: { id: number; first_name: string; last_name: string; site_name?: string }) => {
    setReviewerId(emp.id);
    setReviewerName(`${emp.first_name} ${emp.last_name}${emp.site_name ? ` (${emp.site_name})` : ""}`);
    setRevSearch(`${emp.first_name} ${emp.last_name}`);
    setShowRevResults(false);
  };

  const addObjective = () => setObjectives([...objectives, { objective: "", due_date: "", status: "not_started", category: "" }]);
  const removeObjective = (idx: number) => setObjectives(objectives.filter((_, i) => i !== idx));
  const updateObjective = (idx: number, field: string, value: string) => setObjectives(objectives.map((o, i) => i === idx ? { ...o, [field]: value } : o));

  const addAction = () => setActions([...actions, { action_type: "", custom_label: "", assigned_to_id: null, assigned_to_name: "", due_date: "", status: "not_started", notes: "" }]);
  const removeAction = (idx: number) => setActions(actions.filter((_, i) => i !== idx));
  const updateAction = (idx: number, field: string, value: unknown) => setActions(actions.map((a, i) => i === idx ? { ...a, [field]: value } : a));

  const ALLOWED_TYPES = ["application/pdf", "image/jpeg", "image/png", "application/vnd.openxmlformats-officedocument.wordprocessingml.document"];
  const MAX_FILE_SIZE = 10 * 1024 * 1024;
  const MAX_FILES = 10;

  const addFiles = (incoming: FileList | File[]) => {
    const newFiles = Array.from(incoming).filter((f) => {
      if (!ALLOWED_TYPES.includes(f.type)) return false;
      if (f.size > MAX_FILE_SIZE) return false;
      return true;
    });
    setFiles((prev) => [...prev, ...newFiles].slice(0, MAX_FILES));
  };

  const removeFile = (idx: number) => setFiles((prev) => prev.filter((_, i) => i !== idx));

  const formatFileSize = (bytes: number) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const handleSubmit = async () => {
    if (!employeeId || !reviewerId || !reviewDate || !periodStart || !periodEnd || !summary) {
      setError("Please fill in all required fields.");
      return;
    }
    setSubmitting(true);
    setError("");
    try {
      // Upload files first to get document IDs
      const documentIds: number[] = [];
      if (files.length > 0) {
        const cats = await api.get<{ id: number; name: string }[]>("/api/documents/categories");
        const perfCat = cats.find((c) => c.name === "Performance Reviews");
        const categoryId = perfCat?.id ?? cats[0]?.id ?? 1;

        for (const file of files) {
          const form = new FormData();
          form.append("file", file);
          const cycleName = "Historical Review";
          const params = new URLSearchParams({
            title: `${cycleName} — ${file.name}`,
            category_id: String(categoryId),
            employee_id: String(employeeId),
          });
          const res = await fetch(`/api/documents?${params.toString()}`, {
            method: "POST",
            credentials: "include",
            body: form,
          });
          if (res.ok) {
            const doc = await res.json() as { id: number };
            documentIds.push(doc.id);
          }
        }
      }

      await api.post("/api/reviews/historical", {
        employee_id: employeeId,
        reviewer_id: reviewerId,
        review_date: reviewDate,
        review_period_start: periodStart,
        review_period_end: periodEnd,
        overall_rating: overallRating,
        summary,
        objectives: objectives.length > 0 ? objectives.map((o) => ({ objective: o.objective, due_date: o.due_date || undefined, status: o.status, category: o.category || undefined })) : null,
        actions: actions.length > 0 ? actions.map((a) => ({ action_type: a.action_type === "__custom__" ? "other" : (a.action_type || "other"), commentary: a.action_type === "__custom__" ? a.custom_label + (a.notes ? `: ${a.notes}` : "") : a.notes, assigned_to_id: a.assigned_to_id, due_date: a.due_date || undefined, status: a.status })) : null,
        document_ids: documentIds.length > 0 ? documentIds : null,
      });
      onSaved();
    } catch (err) {
      if (err instanceof ApiError) setError(err.message);
      else setError("Failed to save historical review");
    }
    setSubmitting(false);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" onClick={onClose}>
      <div className="mx-4 w-full max-w-2xl max-h-[90vh] overflow-y-auto rounded-xl bg-white p-6 shadow-xl" onClick={(e) => e.stopPropagation()}>
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Log Historical Review</h3>
        {error && <p className="mb-3 rounded-lg bg-red-50 px-3 py-2 text-sm text-red-600">{error}</p>}

        <div className="space-y-3">
          {/* Employee search */}
          <div className="relative">
            <label className="block text-xs font-medium text-gray-700 mb-1">Employee *</label>
            <input
              type="text"
              value={empSearch}
              onChange={(e) => handleEmpSearchChange(e.target.value)}
              placeholder="Search employee..."
              className="input w-full"
            />
            {employeeName && <p className="text-xs text-green-600 mt-0.5">Selected: {employeeName}</p>}
            {showEmpResults && empResults.length > 0 && (
              <div className="absolute z-10 mt-1 w-full rounded-lg border border-gray-200 bg-white shadow-lg max-h-40 overflow-y-auto">
                {empResults.map((emp) => (
                  <button key={emp.id} type="button" onClick={() => selectEmployee(emp)} className="w-full px-3 py-2 text-left text-sm hover:bg-gray-50">
                    {emp.first_name} {emp.last_name}{emp.site_name ? ` (${emp.site_name})` : ""}
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Review date & period */}
          <div className="grid grid-cols-3 gap-3">
            <label className="block text-xs font-medium text-gray-700">
              Review Date *
              <input type="date" value={reviewDate} onChange={(e) => setReviewDate(e.target.value)} className="input mt-1 w-full" />
            </label>
            <label className="block text-xs font-medium text-gray-700">
              Period Start *
              <input type="date" value={periodStart} onChange={(e) => setPeriodStart(e.target.value)} className="input mt-1 w-full" />
            </label>
            <label className="block text-xs font-medium text-gray-700">
              Period End *
              <input type="date" value={periodEnd} onChange={(e) => setPeriodEnd(e.target.value)} className="input mt-1 w-full" />
            </label>
          </div>

          {/* Reviewer search */}
          <div className="relative">
            <label className="block text-xs font-medium text-gray-700 mb-1">Reviewer / Manager *</label>
            <input
              type="text"
              value={revSearch}
              onChange={(e) => handleRevSearchChange(e.target.value)}
              placeholder="Search reviewer..."
              className="input w-full"
            />
            {reviewerName && <p className="text-xs text-green-600 mt-0.5">Selected: {reviewerName}</p>}
            {showRevResults && revResults.length > 0 && (
              <div className="absolute z-10 mt-1 w-full rounded-lg border border-gray-200 bg-white shadow-lg max-h-40 overflow-y-auto">
                {revResults.map((emp) => (
                  <button key={emp.id} type="button" onClick={() => selectReviewer(emp)} className="w-full px-3 py-2 text-left text-sm hover:bg-gray-50">
                    {emp.first_name} {emp.last_name}{emp.site_name ? ` (${emp.site_name})` : ""}
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Rating */}
          <label className="block text-xs font-medium text-gray-700">
            Overall Rating *
            <select value={overallRating} onChange={(e) => setOverallRating(Number(e.target.value))} className="input mt-1 w-full">
              {[1, 2, 3, 4, 5].map((n) => <option key={n} value={n}>{n}</option>)}
            </select>
          </label>

          {/* Summary */}
          <label className="block text-xs font-medium text-gray-700">
            Summary *
            <textarea value={summary} onChange={(e) => setSummary(e.target.value)} rows={4} className="input mt-1 w-full" placeholder="Review summary..." />
          </label>

          {/* Objectives */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs font-medium text-gray-700">Objectives</span>
              <button type="button" onClick={addObjective} className="rounded border border-gray-300 px-2 py-1 text-xs font-medium text-gray-700 hover:bg-gray-50">+ Add Objective</button>
            </div>
            {objectives.map((obj, idx) => (
              <div key={idx} className="rounded border border-gray-200 p-2 mb-2 space-y-1.5">
                <div className="flex items-start gap-2">
                  <textarea value={obj.objective} onChange={(e) => updateObjective(idx, "objective", e.target.value)} placeholder="Objective" rows={2} className="input flex-1 text-sm" />
                  <button type="button" onClick={() => removeObjective(idx)} className="rounded border border-red-200 px-2 py-1 text-xs text-red-600 hover:bg-red-50">Remove</button>
                </div>
                <div className="flex gap-2">
                  <select value={obj.category} onChange={(e) => updateObjective(idx, "category", e.target.value)} className="input w-36 text-xs">
                    <option value="">Category</option>
                    <option value="performance">Performance</option>
                    <option value="development">Development</option>
                    <option value="training">Training</option>
                    <option value="other">Other</option>
                  </select>
                  <input type="date" value={obj.due_date} onChange={(e) => updateObjective(idx, "due_date", e.target.value)} className="input flex-1 text-xs" />
                  <select value={obj.status} onChange={(e) => updateObjective(idx, "status", e.target.value)} className="input w-28 text-xs">
                    <option value="not_started">Not Started</option>
                    <option value="in_progress">In Progress</option>
                    <option value="completed">Completed</option>
                  </select>
                </div>
              </div>
            ))}
          </div>

          {/* Actions */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs font-medium text-gray-700">Actions</span>
              <button type="button" onClick={addAction} className="rounded border border-gray-300 px-2 py-1 text-xs font-medium text-gray-700 hover:bg-gray-50">+ Add Action</button>
            </div>
            {actions.map((act, idx) => (
              <div key={idx} className="rounded border border-gray-200 p-2 mb-2 space-y-1.5">
                <div className="flex items-start gap-2">
                  <select value={act.action_type} onChange={(e) => updateAction(idx, "action_type", e.target.value)} className="input w-44 text-sm">
                    <option value="">Select action type...</option>
                    {actionTriggers.map((tr) => (
                      <option key={tr.action_code} value={tr.action_code}>{tr.title}</option>
                    ))}
                    <option value="__custom__">Other / Custom</option>
                  </select>
                  <button type="button" onClick={() => removeAction(idx)} className="rounded border border-red-200 px-2 py-1 text-xs text-red-600 hover:bg-red-50">Remove</button>
                </div>
                {act.action_type === "__custom__" && (
                  <input value={act.custom_label} onChange={(e) => updateAction(idx, "custom_label", e.target.value)} placeholder="Custom action description" className="input w-full text-xs" />
                )}
                <div className="flex gap-2">
                  <input type="date" value={act.due_date} onChange={(e) => updateAction(idx, "due_date", e.target.value)} className="input flex-1 text-xs" placeholder="Due date" />
                  <select value={act.status} onChange={(e) => updateAction(idx, "status", e.target.value)} className="input w-28 text-xs">
                    <option value="not_started">Not Started</option>
                    <option value="in_progress">In Progress</option>
                    <option value="completed">Completed</option>
                  </select>
                </div>
                <textarea value={act.notes} onChange={(e) => updateAction(idx, "notes", e.target.value)} placeholder="Notes" rows={2} className="input w-full text-xs" />
              </div>
            ))}
          </div>

          {/* Supporting Documents */}
          <div>
            <span className="text-xs font-medium text-gray-700">Supporting Documents</span>
            <p className="text-[11px] text-gray-400 mb-2">PDF, JPG, PNG, DOCX — max 10 MB per file, up to 10 files</p>

            <div
              onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
              onDragLeave={() => setDragOver(false)}
              onDrop={(e) => { e.preventDefault(); setDragOver(false); if (e.dataTransfer.files.length) addFiles(e.dataTransfer.files); }}
              onClick={() => fileInputRef.current?.click()}
              className={[
                "flex cursor-pointer flex-col items-center justify-center rounded-lg border-2 border-dashed p-4 text-center transition-colors",
                dragOver ? "border-indigo-400 bg-indigo-50" : "border-gray-300 hover:border-gray-400 hover:bg-gray-50",
              ].join(" ")}
            >
              <FileText size={26} strokeWidth={1.5} aria-hidden className="text-gray-400" />
              <span className="mt-1 text-xs text-gray-500">Drop files here or click to upload</span>
              <input
                ref={fileInputRef}
                type="file"
                multiple
                accept=".pdf,.jpg,.jpeg,.png,.docx"
                className="hidden"
                onChange={(e) => { if (e.target.files?.length) { addFiles(e.target.files); e.target.value = ""; } }}
              />
            </div>

            {files.length > 0 && (
              <ul className="mt-2 space-y-1">
                {files.map((f, idx) => (
                  <li key={idx} className="flex items-center justify-between rounded border border-gray-200 bg-white px-3 py-1.5">
                    <div className="min-w-0">
                      <p className="truncate text-xs font-medium text-gray-800">{f.name}</p>
                      <p className="text-[10px] text-gray-400">{formatFileSize(f.size)}</p>
                    </div>
                    <button type="button" onClick={() => removeFile(idx)} className="ml-2 shrink-0 rounded border border-red-200 px-1.5 py-0.5 text-[10px] text-red-600 hover:bg-red-50">Remove</button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>

        <div className="mt-5 flex gap-2 justify-end">
          <button type="button" onClick={onClose} className="rounded-lg border border-gray-300 px-4 py-2 text-sm">Cancel</button>
          <button
            type="button"
            onClick={() => void handleSubmit()}
            disabled={submitting}
            className="rounded-lg bg-indigo-600 px-4 py-2 text-sm font-semibold text-white hover:bg-indigo-700 disabled:opacity-50"
          >
            {submitting ? "Saving..." : "Save Historical Review"}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ---------- Complete Meeting Modal ---------- */

interface MeetingObjective {
  objective: string;
  how_measured: string;
  due_date: string;
}

function CompleteMeetingModal({ reviewId, onClose, onCompleted }: { reviewId: number; onClose: () => void; onCompleted: () => void }) {
  const [meetingDate, setMeetingDate] = useState(new Date().toISOString().slice(0, 10));
  const [agreedRating, setAgreedRating] = useState(3);
  const [meetingNotes, setMeetingNotes] = useState("");
  const [acknowledged, setAcknowledged] = useState(false);
  // Ticked by default: completing the meeting releases feedback unless the
  // manager deliberately unticks it.
  const [releaseFeedback, setReleaseFeedback] = useState(true);
  const [subjectName, setSubjectName] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);
  const [objectives, setObjectives] = useState<MeetingObjective[]>([]);
  const [showObjectives, setShowObjectives] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const data = await api.get<{
          employee_name?: string;
          overall_agreed_rating?: number | null;
          overall_manager_rating?: number | null;
          objectives?: { objective: string; how_measured?: string; due_date?: string }[];
        }>(`/api/reviews/${reviewId}?admin_view=true`);
        if (cancelled) return;
        if (data.employee_name) setSubjectName(data.employee_name);
        if (data.overall_agreed_rating != null) setAgreedRating(data.overall_agreed_rating);
        else if (data.overall_manager_rating != null) setAgreedRating(data.overall_manager_rating);
        if (data.objectives?.length) {
          setObjectives(data.objectives.map((o) => ({
            objective: o.objective,
            how_measured: o.how_measured || "",
            due_date: o.due_date || "",
          })));
          setShowObjectives(true);
        }
      } catch { /* ignore load errors */ }
      if (!cancelled) setLoading(false);
    })();
    return () => { cancelled = true; };
  }, [reviewId]);

  const handleSubmit = async () => {
    if (!acknowledged) {
      setError("Please confirm the employee has been shown the review.");
      return;
    }
    setSubmitting(true);
    setError("");
    try {
      await api.post(`/api/reviews/${reviewId}/complete`, {
        meeting_date: meetingDate,
        overall_agreed_rating: agreedRating,
        meeting_notes: meetingNotes || null,
        confirmed_objectives: objectives.length ? objectives : undefined,
        release_feedback: releaseFeedback,
      });
      onCompleted();
    } catch (err) {
      if (err instanceof ApiError) setError(err.message);
      else setError("Failed to complete review");
    }
    setSubmitting(false);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 overflow-y-auto py-8" onClick={onClose}>
      <div className="mx-4 w-full max-w-lg rounded-xl bg-white p-6 shadow-xl max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <h3 className="text-lg font-semibold text-gray-900 mb-1">Complete Meeting</h3>
        <p className="mb-4 text-xs text-gray-500">
          Managers normally finish the appraisal in the{" "}
          <a href={`/review?review=${reviewId}&tab=meeting`} className="font-semibold text-pme-blue hover:underline">meeting guide</a>,
          which also sets up the catch-ups. Use this form only if it has to be closed from HR.
        </p>
        {error && <p className="mb-3 rounded-lg bg-red-50 px-3 py-2 text-sm text-red-600">{error}</p>}

        {loading ? (
          <p className="text-sm text-gray-500 py-4 text-center">Loading review data...</p>
        ) : (
          <div className="space-y-4">
            <label className="block text-xs font-medium text-gray-700">
              Meeting Date
              <input type="date" value={meetingDate} onChange={(e) => setMeetingDate(e.target.value)} className="input mt-1 w-full" />
            </label>

            <label className="block text-xs font-medium text-gray-700">
              Agreed Overall Rating
              <select value={agreedRating} onChange={(e) => setAgreedRating(Number(e.target.value))} className="input mt-1 w-full">
                {[1, 2, 3, 4, 5].map((n) => <option key={n} value={n}>{n}</option>)}
              </select>
            </label>

            <label className="block text-xs font-medium text-gray-700">
              Meeting Notes
              <textarea value={meetingNotes} onChange={(e) => setMeetingNotes(e.target.value)} rows={3} className="input mt-1 w-full" placeholder="Notes from the review meeting..." />
            </label>

            {/* Confirmed Objectives */}
            {objectives.length > 0 && (
              <div className="border border-gray-200 rounded-lg">
                <button type="button" onClick={() => setShowObjectives(!showObjectives)} className="flex w-full items-center justify-between px-3 py-2 text-xs font-semibold text-gray-700 hover:bg-gray-50">
                  <span>Confirmed Objectives ({objectives.length})</span>
                  <span className="text-gray-400">{showObjectives ? "−" : "+"}</span>
                </button>
                {showObjectives && (
                  <div className="border-t border-gray-200 px-3 py-2 space-y-2">
                    {objectives.map((o, idx) => (
                      <div key={idx} className="rounded border border-gray-100 bg-gray-50 p-2 text-xs">
                        <input
                          value={o.objective}
                          onChange={(e) => {
                            const next = [...objectives];
                            next[idx] = { ...next[idx], objective: e.target.value };
                            setObjectives(next);
                          }}
                          className="w-full rounded border border-gray-200 px-2 py-1 text-xs mb-1"
                          placeholder="Objective"
                        />
                        <div className="flex gap-1">
                          <input
                            value={o.how_measured}
                            onChange={(e) => {
                              const next = [...objectives];
                              next[idx] = { ...next[idx], how_measured: e.target.value };
                              setObjectives(next);
                            }}
                            className="flex-1 rounded border border-gray-200 px-2 py-1 text-xs"
                            placeholder="How measured"
                          />
                          <input
                            type="date"
                            value={o.due_date}
                            onChange={(e) => {
                              const next = [...objectives];
                              next[idx] = { ...next[idx], due_date: e.target.value };
                              setObjectives(next);
                            }}
                            className="w-32 rounded border border-gray-200 px-2 py-1 text-xs"
                          />
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            <label className="flex items-start gap-2 cursor-pointer">
              <input type="checkbox" checked={acknowledged} onChange={(e) => setAcknowledged(e.target.checked)} className="mt-0.5 h-4 w-4 rounded border-gray-300" />
              <span className="text-xs text-gray-700">Employee has been shown and discussed the review</span>
            </label>

            {/* Feedback release gate — ticked by default. Unticking completes
                the meeting without revealing manager content to the employee;
                it can be released later from the review page. */}
            <label className="flex items-start gap-2 cursor-pointer rounded-lg border border-amber-200 bg-amber-50/60 p-2.5">
              <input
                type="checkbox"
                checked={releaseFeedback}
                onChange={(e) => setReleaseFeedback(e.target.checked)}
                className="mt-0.5 h-4 w-4 rounded border-gray-300"
              />
              <span className="text-xs text-gray-800">
                Release my feedback to {subjectName || "the employee"} — they will see the
                review content once you confirm.
                {!releaseFeedback && (
                  <span className="mt-1 block text-[11px] text-amber-700">
                    Not releasing: the review completes but stays hidden from them. You can
                    release it later from the review page.
                  </span>
                )}
              </span>
            </label>
          </div>
        )}

        <div className="mt-5 flex gap-2 justify-end">
          <button type="button" onClick={onClose} className="rounded-lg border border-gray-300 px-4 py-2 text-sm">Cancel</button>
          <button
            type="button"
            onClick={() => void handleSubmit()}
            disabled={submitting || !acknowledged || loading}
            className="rounded-lg bg-green-600 px-4 py-2 text-sm font-semibold text-white hover:bg-green-700 disabled:opacity-50"
          >
            {submitting ? "Completing..." : "Complete Review"}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ---------- Pending peer feedback summary ---------- */

/**
 * Inline "waiting on" line for a review. Renders nothing unless there are
 * peers still outstanding, so completed/no-peer reviews stay uncluttered.
 * Peers past their deadline are shown muted since they no longer block the
 * manager from submitting.
 */
function PendingPeersLine({ status }: { status: ColleagueStatus | undefined }) {
  if (!status) return null;
  const total = status.submitted.length + status.pending.length;
  if (total === 0) return null;
  const blocking = status.pending.filter((p) => !p.expired);
  const expired = status.pending.filter((p) => p.expired);
  return (
    <div className="mt-1.5 text-xs">
      <span className="font-medium text-gray-500">
        Peer feedback: {status.submitted.length}/{total} submitted
      </span>
      {blocking.length > 0 && (
        <span className="text-amber-700">
          {" · Waiting on: "}
          {blocking.map((p) => p.name).join(", ")}
        </span>
      )}
      {expired.length > 0 && (
        <span className="text-gray-400" title="Deadline passed — no longer blocks the manager review">
          {" · Overdue (not blocking): "}
          {expired.map((p) => p.name).join(", ")}
        </span>
      )}
    </div>
  );
}

/* ---------- Cycle Review Card ---------- */

function CycleReviewCard({
  cr,
  statusBadge,
  onCancel,
  onDelete,
  onCompleteMeeting,
  colleagueStatus,
  highlighted,
}: {
  cr: CycleReview;
  statusBadge: (status: string) => string;
  onCancel: (id: number, name: string) => Promise<void>;
  onDelete: (id: number) => Promise<void>;
  onCompleteMeeting: (id: number) => void;
  colleagueStatus?: ColleagueStatus;
  highlighted?: boolean;
}) {
  const [showColleagues, setShowColleagues] = useState(false);
  const [colleagues, setColleagues] = useState<ColleagueFeedbackItem[]>([]);
  const [loadingColleagues, setLoadingColleagues] = useState(false);
  const [addingColleague, setAddingColleague] = useState(false);
  const [removingId, setRemovingId] = useState<number | null>(null);

  const canManageColleagues =
    cr.status === "self_assessment_submitted" || cr.status === "colleague_feedback_in_progress";
  const canStartManagerReview = cr.status === "manager_review_in_progress" || cr.status === "colleague_feedback_complete";

  const loadColleagues = useCallback(async () => {
    setLoadingColleagues(true);
    try {
      // NB: this endpoint returns { colleagues, focus_areas } — not a bare array.
      const data = await api.get<{ colleagues: ColleagueFeedbackItem[]; focus_areas: string[] | null }>(`/api/reviews/${cr.id}/colleagues`);
      setColleagues(data?.colleagues ?? []);
    } catch { /* ignore */ }
    setLoadingColleagues(false);
  }, [cr.id]);

  const handleToggleColleagues = () => {
    if (!showColleagues) {
      setShowColleagues(true);
      void loadColleagues();
    } else {
      setShowColleagues(false);
    }
  };

  const handleAddColleague = async (colleagueId: number | null) => {
    if (!colleagueId) return;
    setAddingColleague(true);
    try {
      await api.post(`/api/reviews/${cr.id}/colleagues`, {
        colleague_id: colleagueId,
        focus_area: "general",
      });
      void loadColleagues();
    } catch (err) {
      if (err instanceof ApiError) alert(err.message);
    }
    setAddingColleague(false);
  };

  const handleRemoveColleague = async (feedbackId: number) => {
    setRemovingId(feedbackId);
    try {
      await api.delete(`/api/reviews/colleagues/${feedbackId}`);
      setColleagues((prev) => prev.filter((c) => c.id !== feedbackId));
    } catch (err) {
      if (err instanceof ApiError) alert(err.message);
    }
    setRemovingId(null);
  };

  const colleagueStatusBadge = (status: string) => {
    switch (status) {
      case "submitted":
        return "bg-green-50 text-green-700";
      case "pending":
        return "bg-amber-50 text-amber-800";
      default:
        return "bg-gray-100 text-gray-600";
    }
  };

  return (
    <div
      className={`rounded-lg border p-3 transition-colors ${cr.status === "cancelled" ? "border-gray-200 bg-gray-100 opacity-60" : highlighted ? "border-pme-blue bg-blue-50/50 ring-1 ring-pme-blue/20" : "border-gray-100 bg-gray-50 hover:bg-gray-100"}`}
    >
      <Link
        to={`/review?review=${cr.id}`}
        className="block"
      >
        <div className="flex items-center justify-between gap-2">
          <div className="min-w-0">
            <p className={`text-sm font-medium ${cr.status === "cancelled" ? "text-gray-400 line-through" : "text-gray-900"}`}>
              {cr.employee_name}
              {cr.is_historical && (
                <span className="ml-2 inline-block rounded-full bg-indigo-100 px-2 py-0.5 text-[10px] font-semibold text-indigo-700">Historical</span>
              )}
              {(cr.document_count ?? 0) > 0 && (
                <span className="ml-1.5 inline-block rounded-full bg-gray-100 px-2 py-0.5 text-[10px] font-semibold text-gray-600">{cr.document_count} doc{cr.document_count !== 1 ? "s" : ""}</span>
              )}
            </p>
            <p className="text-xs text-gray-500">
              Manager: {cr.manager_name ?? "–"}
              {cr.site_name && <span className="ml-2">· {cr.site_name}</span>}
              {cr.department_name && <span className="ml-1">/ {cr.department_name}</span>}
            </p>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            {cr.deadline && (
              <span className={`text-[10px] ${reviewIsOverdue(cr) ? "font-semibold text-red-600" : "text-gray-400"}`}>
                {reviewIsOverdue(cr) ? "Overdue · was due" : "Due:"} {cr.deadline}
              </span>
            )}
            <span className={`rounded-full px-2 py-0.5 text-[10px] font-semibold ${statusBadge(cr.status)}`}>
              {reviewStatusLabel(cr.status)}
            </span>
          </div>
        </div>
        {!cr.is_historical && cr.status !== "cancelled" && (
          <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1">
            <ReviewStagePips status={cr.status} />
            <span className="text-[11px] text-gray-500">{reviewStatusNext(cr.status)}</span>
          </div>
        )}
        <div className="mt-2 flex gap-3 text-xs text-gray-500">
          <span>Self: {selfRatingText(cr.overall_self_rating ?? cr.self_rating, cr.rating_words)}</span>
          <span>Peer: {cr.peer_rating != null ? (cr.rating_words ? `${cr.peer_rating} of 4` : `${cr.peer_rating}/5`) : "–"}</span>
          <span>Manager: {(cr.overall_manager_rating ?? cr.manager_rating) != null ? `${cr.overall_manager_rating ?? cr.manager_rating}/5` : "–"}</span>
        </div>
      </Link>
      <PendingPeersLine status={colleagueStatus} />
      <div className="mt-2 flex gap-1">
        {cr.status !== "completed" && cr.status !== "cancelled" && (
          <button
            type="button"
            onClick={() => { if (cr.id != null) void onCancel(cr.id, cr.employee_name); }}
            className="rounded border border-amber-300 px-2 py-1 text-xs font-medium text-amber-700 hover:bg-amber-50"
          >
            Cancel
          </button>
        )}
        {(cr.status === "not_started" || cr.status === "cancelled") && (
          <button
            type="button"
            onClick={() => { if (cr.id != null) void onDelete(cr.id); }}
            className="rounded border border-red-200 px-2 py-1 text-xs font-medium text-red-600 hover:bg-red-50"
          >
            Delete
          </button>
        )}
        {canManageColleagues && (
          <button
            type="button"
            onClick={handleToggleColleagues}
            className="rounded border border-blue-300 px-2 py-1 text-xs font-medium text-blue-700 hover:bg-blue-50"
          >
            {showColleagues ? "Hide Colleagues" : "Manage Colleagues"}
          </button>
        )}
        {canStartManagerReview && (
          <Link
            to={`/review?review=${cr.id}&tab=manager-review&step=1`}
            className="rounded border border-green-300 px-2 py-1 text-xs font-medium text-green-700 hover:bg-green-50 inline-block"
          >
            {cr.status === "manager_review_in_progress" ? "Continue Manager Review" : "Start Manager Review"}
          </Link>
        )}
        {(cr.status === "meeting_pending" || cr.status === "employee_response_pending" || cr.status === "manager_review_submitted") && (
          <button
            type="button"
            onClick={() => { if (cr.id != null) onCompleteMeeting(cr.id); }}
            className="rounded border border-green-400 bg-green-50 px-2 py-1 text-xs font-medium text-green-700 hover:bg-green-100"
          >
            Complete Meeting
          </button>
        )}
      </div>

      {showColleagues && canManageColleagues && (
        <div className="mt-3 border-t border-gray-200 pt-3">
          <p className="text-xs font-semibold text-gray-700 mb-2">Colleague Feedback</p>
          {loadingColleagues ? (
            <p className="text-xs text-gray-400">Loading...</p>
          ) : (
            <>
              {colleagues.length === 0 && (
                <p className="text-xs text-gray-400 mb-2">No colleagues assigned yet.</p>
              )}
              <div className="space-y-1.5 mb-3">
                {colleagues.map((col) => (
                  <div
                    key={col.id}
                    className="flex items-center justify-between rounded border border-gray-100 bg-white px-2.5 py-1.5"
                  >
                    <div className="min-w-0">
                      <span className="text-xs font-medium text-gray-800">{col.colleague_name}</span>
                      {col.focus_area && col.focus_area !== "general" && (
                        <span className="ml-1.5 text-[10px] text-gray-400">({col.focus_area})</span>
                      )}
                    </div>
                    <div className="flex items-center gap-1.5 shrink-0">
                      <span className={`rounded-full px-1.5 py-0.5 text-[10px] font-semibold ${colleagueStatusBadge(col.status)}`}>
                        {col.status}
                      </span>
                      {col.status !== "submitted" && col.invited_at && cr.status !== "completed" && cr.status !== "cancelled" && (
                        <ResendColleagueButton feedbackId={col.id} name={col.colleague_name} />
                      )}
                      {col.status !== "submitted" && (
                        <button
                          type="button"
                          onClick={() => void handleRemoveColleague(col.id)}
                          disabled={removingId === col.id}
                          className="rounded border border-red-200 px-1.5 py-0.5 text-[10px] font-medium text-red-600 hover:bg-red-50 disabled:opacity-50"
                        >
                          {removingId === col.id ? "..." : "Remove"}
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
              <div className="flex items-center gap-2">
                <div className="flex-1">
                  <EmployeeSearch
                    value={null}
                    onChange={(id) => void handleAddColleague(id)}
                    placeholder="Add colleague..."
                    className="input text-xs"
                    disabled={addingColleague}
                    excludeIds={[cr.employee_id, ...colleagues.map((c) => c.colleague_id)]}
                  />
                </div>
                {addingColleague && <span className="text-[10px] text-gray-400">Adding...</span>}
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}

/* ---------- 360 Template Meta Form (create only) ---------- */

function TemplateMetaForm({ onSaved, onCancel }: { onSaved: () => void; onCancel: () => void }) {
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [reviewType, setReviewType] = useState("annual");
  const [isActive, setIsActive] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    setError("");
    const payload = { name, description: description || null, review_type: reviewType, is_active: isActive };
    try {
      await api.post("/api/reviews/templates", payload);
      onSaved();
    } catch (err) {
      if (err instanceof ApiError) setError(err.message);
      else setError("Failed to create template");
    }
    setSubmitting(false);
  };

  return (
    <form onSubmit={submit} className="rounded-xl border border-gray-200 bg-white p-5 space-y-3">
      <h3 className="text-sm font-semibold text-gray-900">New Review Template</h3>
      {error && <p className="mb-2 rounded-lg bg-red-50 px-3 py-2 text-sm text-red-600">{error}</p>}
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <label className="block text-sm">
          <span className="text-xs font-medium text-gray-700">Name</span>
          <input value={name} onChange={(e) => setName(e.target.value)} required placeholder="2026 appraisals" className="input mt-1" />
        </label>
        <label className="block text-sm">
          <span className="text-xs font-medium text-gray-700">Review Type</span>
          <select value={reviewType} onChange={(e) => setReviewType(e.target.value)} className="input mt-1">
            <option value="annual">Annual</option>
            <option value="probation">Probation</option>
            <option value="pip">PIP</option>
            <option value="mid_year">Mid-Year</option>
            <option value="adhoc">Ad Hoc</option>
          </select>
        </label>
      </div>
      <label className="block text-sm">
        <span className="text-xs font-medium text-gray-700">Description</span>
        <textarea value={description} onChange={(e) => setDescription(e.target.value)} rows={2} className="input mt-1" placeholder="Template description..." />
      </label>
      <label className="flex items-center gap-2 text-xs text-gray-700 cursor-pointer">
        <input type="checkbox" checked={isActive} onChange={(e) => setIsActive(e.target.checked)} className="h-3.5 w-3.5 rounded border-gray-300" />
        Active
      </label>
      <div className="flex gap-2">
        <button type="submit" disabled={submitting} className="rounded-lg bg-pme-blue px-4 py-2 text-sm font-semibold text-white disabled:opacity-60">
          {submitting ? "Saving..." : "Create Template"}
        </button>
        <button type="button" onClick={onCancel} className="rounded-lg border border-gray-300 px-4 py-2 text-sm">Cancel</button>
      </div>
    </form>
  );
}

/* ---------- Template Editor (full question management) ---------- */

interface TemplateQuestion {
  id?: number;
  target: string;
  section_name: string;
  question_text: string | null;
  instructions_text: string | null;
  question_type: string;
  includes_rating: boolean;
  is_required: boolean;
  sort_order: number;
}

interface TemplateDetail {
  id: number;
  name: string;
  description: string | null;
  review_type: string;
  is_active: boolean;
  questions: TemplateQuestion[];
}

const QUESTION_TYPES = [
  { value: "text", label: "Text", color: "bg-blue-100 text-blue-700" },
  { value: "rating_1_5", label: "Rating 1-5", color: "bg-amber-100 text-amber-700" },
  { value: "rating_1_10", label: "Rating 1-10", color: "bg-orange-100 text-orange-700" },
  { value: "yes_no", label: "Yes / No", color: "bg-green-100 text-green-700" },
  { value: "table", label: "Table", color: "bg-purple-100 text-purple-700" },
  { value: "behaviours", label: "Behaviours", color: "bg-pink-100 text-pink-700" },
] as const;

const TARGET_TABS = [
  { value: "employee", label: "Employee Questions" },
  { value: "colleague", label: "Colleague Questions" },
  { value: "manager", label: "Manager Sections" },
] as const;

function questionTypeBadge(qt: string) {
  const found = QUESTION_TYPES.find((t) => t.value === qt);
  if (!found) return <span className="rounded-full bg-gray-100 px-2 py-0.5 text-[10px] font-semibold text-gray-500">{qt}</span>;
  return <span className={`rounded-full px-2 py-0.5 text-[10px] font-semibold ${found.color}`}>{found.label}</span>;
}

function TemplateEditor({ templateId, onClose, onSaved }: { templateId: number; onClose: () => void; onSaved: () => void }) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);

  // Template metadata
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [reviewType, setReviewType] = useState("annual");
  const [isActive, setIsActive] = useState(true);

  // Questions
  const [questions, setQuestions] = useState<TemplateQuestion[]>([]);
  const [activeTab, setActiveTab] = useState<"employee" | "colleague" | "manager">("employee");
  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [addingNew, setAddingNew] = useState(false);
  const [previewTab, setPreviewTab] = useState<string | null>(null);

  // Inline edit form state
  const [editSectionName, setEditSectionName] = useState("");
  const [editQuestionText, setEditQuestionText] = useState("");
  const [editInstructionsText, setEditInstructionsText] = useState("");
  const [editQuestionType, setEditQuestionType] = useState("text");
  const [editIncludesRating, setEditIncludesRating] = useState(false);
  const [editIsRequired, setEditIsRequired] = useState(true);

  // Per-step employee visibility (steps 10-12 are permanently locked)
  const [stepVis, setStepVis] = useState<{ step_number: number; visible_to_employee: boolean; locked: boolean }[]>([]);
  const [stepVisSaving, setStepVisSaving] = useState(false);
  const [stepVisMsg, setStepVisMsg] = useState("");

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      setError("");
      try {
        const data = await api.get<TemplateDetail>(`/api/reviews/templates/${templateId}`);
        if (cancelled) return;
        setName(data.name);
        setDescription(data.description ?? "");
        setReviewType(data.review_type);
        setIsActive(data.is_active);
        setQuestions((data.questions ?? []).sort((a, b) => a.sort_order - b.sort_order));
        try {
          const vis = await api.get<{ steps: { step_number: number; visible_to_employee: boolean; locked: boolean }[] }>(
            `/api/settings/review-templates/${templateId}/step-visibility`);
          if (!cancelled) setStepVis(vis?.steps ?? []);
        } catch { /* non-critical */ }
      } catch (err) {
        if (cancelled) return;
        if (err instanceof ApiError) setError(err.message);
        else setError("Failed to load template");
      }
      setLoading(false);
    };
    void load();
    return () => { cancelled = true; };
  }, [templateId]);

  const tabQuestions = questions.filter((q) => q.target === activeTab).sort((a, b) => a.sort_order - b.sort_order);

  const startEdit = (globalIndex: number) => {
    const q = questions[globalIndex];
    setEditSectionName(q.section_name);
    setEditQuestionText(q.question_text ?? "");
    setEditInstructionsText(q.instructions_text ?? "");
    setEditQuestionType(q.question_type);
    setEditIncludesRating(q.includes_rating);
    setEditIsRequired(q.is_required);
    setEditingIndex(globalIndex);
    setAddingNew(false);
  };

  const startAdd = () => {
    setEditSectionName("");
    setEditQuestionText("");
    setEditInstructionsText("");
    setEditQuestionType("text");
    setEditIncludesRating(false);
    setEditIsRequired(true);
    setAddingNew(true);
    setEditingIndex(null);
  };

  const cancelEdit = () => {
    setEditingIndex(null);
    setAddingNew(false);
  };

  const saveEditedQuestion = () => {
    if (!editSectionName.trim()) return;
    if (editingIndex !== null) {
      setQuestions((prev) => prev.map((q, i) => i === editingIndex ? {
        ...q,
        section_name: editSectionName.trim(),
        question_text: editQuestionText.trim() || null,
        instructions_text: editInstructionsText.trim() || null,
        question_type: editQuestionType,
        includes_rating: editIncludesRating,
        is_required: editIsRequired,
      } : q));
      setEditingIndex(null);
    }
  };

  const saveNewQuestion = () => {
    if (!editSectionName.trim()) return;
    const tabQs = questions.filter((q) => q.target === activeTab);
    const maxSort = tabQs.length > 0 ? Math.max(...tabQs.map((q) => q.sort_order)) : -1;
    const newQ: TemplateQuestion = {
      target: activeTab,
      section_name: editSectionName.trim(),
      question_text: editQuestionText.trim() || null,
      instructions_text: editInstructionsText.trim() || null,
      question_type: editQuestionType,
      includes_rating: editIncludesRating,
      is_required: editIsRequired,
      sort_order: maxSort + 1,
    };
    setQuestions((prev) => [...prev, newQ]);
    setAddingNew(false);
  };

  const deleteQuestion = (globalIndex: number) => {
    setQuestions((prev) => prev.filter((_, i) => i !== globalIndex));
    if (editingIndex === globalIndex) setEditingIndex(null);
  };

  const moveQuestion = (globalIndex: number, direction: "up" | "down") => {
    const q = questions[globalIndex];
    const tabQs = questions
      .map((qq, i) => ({ qq, i }))
      .filter((x) => x.qq.target === q.target)
      .sort((a, b) => a.qq.sort_order - b.qq.sort_order);

    const posInTab = tabQs.findIndex((x) => x.i === globalIndex);
    if (posInTab < 0) return;
    const swapPos = direction === "up" ? posInTab - 1 : posInTab + 1;
    if (swapPos < 0 || swapPos >= tabQs.length) return;

    const swapGlobalIndex = tabQs[swapPos].i;
    setQuestions((prev) => {
      const next = [...prev];
      const tmpSort = next[globalIndex].sort_order;
      next[globalIndex] = { ...next[globalIndex], sort_order: next[swapGlobalIndex].sort_order };
      next[swapGlobalIndex] = { ...next[swapGlobalIndex], sort_order: tmpSort };
      return next;
    });
  };

  const saveTemplate = async () => {
    setSaving(true);
    setError("");
    setSaveSuccess(false);
    // Recalculate sort_order per target
    const reordered: TemplateQuestion[] = [];
    for (const target of ["employee", "colleague", "manager"]) {
      const tqs = questions
        .filter((q) => q.target === target)
        .sort((a, b) => a.sort_order - b.sort_order);
      tqs.forEach((q, idx) => {
        reordered.push({ ...q, sort_order: idx });
      });
    }
    const payload = {
      name,
      description: description || null,
      review_type: reviewType,
      is_active: isActive,
      questions: reordered.map(({ id: _id, ...rest }) => rest),
    };
    try {
      await api.put(`/api/reviews/templates/${templateId}`, payload);
      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 3000);
    } catch (err) {
      if (err instanceof ApiError) setError(err.message);
      else setError("Failed to save template");
    }
    setSaving(false);
  };

  const saveAndClose = async () => {
    await saveTemplate();
    if (!error) onSaved();
  };

  if (loading) {
    return (
      <div className="rounded-xl border border-gray-200 bg-white p-6">
        <p className="text-sm text-gray-400">Loading template...</p>
      </div>
    );
  }

  const renderQuestionForm = (onSave: () => void) => (
    <div className="rounded-lg border border-indigo-200 bg-indigo-50/30 p-4 space-y-3">
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <label className="block text-sm">
          <span className="text-xs font-medium text-gray-700">Section name *</span>
          <input
            value={editSectionName}
            onChange={(e) => setEditSectionName(e.target.value)}
            required
            placeholder="e.g. Key Responsibilities"
            className="input mt-1"
          />
        </label>
        <label className="block text-sm">
          <span className="text-xs font-medium text-gray-700">Question type</span>
          <select value={editQuestionType} onChange={(e) => setEditQuestionType(e.target.value)} className="input mt-1">
            {QUESTION_TYPES.map((qt) => (
              <option key={qt.value} value={qt.value}>{qt.label}</option>
            ))}
          </select>
        </label>
      </div>
      <label className="block text-sm">
        <span className="text-xs font-medium text-gray-700">Question text</span>
        <textarea
          value={editQuestionText}
          onChange={(e) => setEditQuestionText(e.target.value)}
          rows={2}
          className="input mt-1"
          placeholder="The question prompt shown to the reviewer..."
        />
      </label>
      <label className="block text-sm">
        <span className="text-xs font-medium text-gray-700">Instructions / help text</span>
        <textarea
          value={editInstructionsText}
          onChange={(e) => setEditInstructionsText(e.target.value)}
          rows={2}
          className="input mt-1"
          placeholder="Optional guidance shown below the question..."
        />
      </label>
      <div className="flex items-center gap-4">
        <label className="flex items-center gap-2 text-xs text-gray-700 cursor-pointer">
          <input type="checkbox" checked={editIncludesRating} onChange={(e) => setEditIncludesRating(e.target.checked)} className="h-3.5 w-3.5 rounded border-gray-300" />
          Includes rating
        </label>
        <label className="flex items-center gap-2 text-xs text-gray-700 cursor-pointer">
          <input type="checkbox" checked={editIsRequired} onChange={(e) => setEditIsRequired(e.target.checked)} className="h-3.5 w-3.5 rounded border-gray-300" />
          Required
        </label>
      </div>
      <div className="flex gap-2">
        <button type="button" onClick={onSave} disabled={!editSectionName.trim()} className="rounded-lg bg-indigo-600 px-3 py-1.5 text-xs font-semibold text-white disabled:opacity-50 hover:bg-indigo-700">
          {editingIndex !== null ? "Update" : "Add"}
        </button>
        <button type="button" onClick={cancelEdit} className="rounded-lg border border-gray-300 px-3 py-1.5 text-xs text-gray-600 hover:bg-gray-50">Cancel</button>
      </div>
    </div>
  );

  const renderPreview = (target: string) => {
    const tqs = questions.filter((q) => q.target === target).sort((a, b) => a.sort_order - b.sort_order);
    if (tqs.length === 0) return <p className="text-sm text-gray-400 italic">No questions in this section.</p>;
    // Group by section_name
    const sections: { name: string; items: TemplateQuestion[] }[] = [];
    for (const q of tqs) {
      const existing = sections.find((s) => s.name === q.section_name);
      if (existing) existing.items.push(q);
      else sections.push({ name: q.section_name, items: [q] });
    }
    return (
      <div className="space-y-4">
        {sections.map((sec) => (
          <div key={sec.name} className="rounded-lg border border-gray-200 bg-gray-50 p-4">
            <h4 className="text-sm font-semibold text-gray-900 mb-2">{sec.name}</h4>
            <div className="space-y-2">
              {sec.items.map((q, i) => (
                <div key={i} className="text-sm text-gray-700">
                  <div className="flex items-start gap-2">
                    <span className="text-gray-400 text-xs mt-0.5 shrink-0">{i + 1}.</span>
                    <div className="min-w-0 flex-1">
                      <p>{q.question_text || <span className="italic text-gray-400">(no question text)</span>}</p>
                      {q.instructions_text && <p className="text-xs text-gray-400 mt-0.5 italic">{q.instructions_text}</p>}
                      <div className="flex items-center gap-2 mt-1">
                        {questionTypeBadge(q.question_type)}
                        {q.includes_rating && <span className="text-[10px] text-amber-600 font-medium">+ Rating</span>}
                        {q.is_required && <span className="text-[10px] text-red-500 font-medium">Required</span>}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    );
  };

  return (
    <div className="space-y-4">
      {/* Back / close */}
      <button type="button" onClick={onClose} className="flex items-center gap-1 text-sm text-gray-500 hover:text-gray-700">
        <span>&larr;</span> Back to templates
      </button>

      {error && <p className="rounded-lg bg-red-50 px-3 py-2 text-sm text-red-600">{error}</p>}
      {saveSuccess && <p className="rounded-lg bg-green-50 px-3 py-2 text-sm text-green-600">Template saved successfully.</p>}

      {/* Template metadata */}
      <div className="rounded-xl border border-gray-200 bg-white p-5 space-y-3">
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
          <label className="block text-sm sm:col-span-2">
            <span className="text-xs font-medium text-gray-700">Template name</span>
            <input value={name} onChange={(e) => setName(e.target.value)} required className="input mt-1" />
          </label>
          <label className="block text-sm">
            <span className="text-xs font-medium text-gray-700">Review type</span>
            <select value={reviewType} onChange={(e) => setReviewType(e.target.value)} className="input mt-1">
              <option value="annual">Annual</option>
              <option value="probation">Probation</option>
              <option value="pip">PIP</option>
              <option value="mid_year">Mid-Year</option>
              <option value="adhoc">Ad Hoc</option>
            </select>
          </label>
        </div>
        <label className="block text-sm">
          <span className="text-xs font-medium text-gray-700">Description</span>
          <textarea value={description} onChange={(e) => setDescription(e.target.value)} rows={2} className="input mt-1" placeholder="Template description..." />
        </label>
        <label className="flex items-center gap-2 text-xs text-gray-700 cursor-pointer">
          <input type="checkbox" checked={isActive} onChange={(e) => setIsActive(e.target.checked)} className="h-3.5 w-3.5 rounded border-gray-300" />
          Active
        </label>
      </div>

      {/* Per-step employee visibility */}
      <div className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
        <h4 className="text-sm font-semibold text-gray-900">Employee visibility after completion</h4>
        <p className="mt-1 text-xs text-gray-500">
          Controls which manager review steps the employee can see once their review is
          <strong> completed</strong>. Nothing is visible before completion. Unticked means hidden.
        </p>
        {stepVisMsg && <p className="mt-2 text-xs text-green-700">{stepVisMsg}</p>}
        <div className="mt-3 grid grid-cols-1 gap-1.5 sm:grid-cols-2 lg:grid-cols-3">
          {stepVis.map((sv) => (
            <label
              key={sv.step_number}
              className={`flex items-center gap-2 rounded border px-2.5 py-1.5 text-xs ${
                sv.locked ? "border-gray-200 bg-gray-50 text-gray-400" : "border-gray-200 text-gray-700"
              }`}
              title={sv.locked
                ? (sv.step_number === 12
                    ? "CONFIDENTIAL — risk & succession is never shown to the employee"
                    : "Never shown to the employee")
                : undefined}
            >
              <input
                type="checkbox"
                disabled={sv.locked}
                checked={!sv.locked && sv.visible_to_employee}
                onChange={(e) => setStepVis((prev) => prev.map((x) =>
                  x.step_number === sv.step_number ? { ...x, visible_to_employee: e.target.checked } : x))}
                className="h-3.5 w-3.5 rounded border-gray-300 disabled:opacity-50"
              />
              <span>
                Step {sv.step_number}
                {sv.locked && (
                  <span className="ml-1.5 font-semibold uppercase tracking-wide text-[10px] text-red-500">
                    {sv.step_number === 12 ? "Confidential" : "Never shown"}
                  </span>
                )}
              </span>
            </label>
          ))}
        </div>
        <button
          type="button"
          disabled={stepVisSaving || stepVis.length === 0}
          onClick={async () => {
            setStepVisSaving(true); setStepVisMsg("");
            try {
              const payload = stepVis
                .filter((s) => !s.locked)
                .map((s) => ({ step_number: s.step_number, visible_to_employee: s.visible_to_employee }));
              const fresh = await api.put<{ steps: typeof stepVis }>(
                `/api/settings/review-templates/${templateId}/step-visibility`, payload);
              setStepVis(fresh?.steps ?? stepVis);
              setStepVisMsg("Visibility saved.");
            } catch (err) {
              setStepVisMsg(err instanceof ApiError ? err.message : "Save failed");
            } finally {
              setStepVisSaving(false);
            }
          }}
          className="mt-3 rounded-lg border border-indigo-300 px-3 py-1.5 text-xs font-medium text-indigo-700 hover:bg-indigo-50 disabled:opacity-50"
        >
          {stepVisSaving ? "Saving..." : "Save visibility"}
        </button>
      </div>

      {/* Question tabs */}
      <div className="rounded-xl border border-gray-200 bg-white shadow-sm overflow-hidden">
        <div className="flex border-b border-gray-200">
          {TARGET_TABS.map((tt) => {
            const count = questions.filter((q) => q.target === tt.value).length;
            return (
              <button
                key={tt.value}
                type="button"
                onClick={() => { setActiveTab(tt.value as typeof activeTab); setEditingIndex(null); setAddingNew(false); setPreviewTab(null); }}
                className={`flex-1 px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ${
                  activeTab === tt.value
                    ? "border-indigo-600 text-indigo-600 bg-indigo-50/50"
                    : "border-transparent text-gray-500 hover:text-gray-700 hover:bg-gray-50"
                }`}
              >
                {tt.label}
                <span className={`ml-1.5 rounded-full px-1.5 py-0.5 text-[10px] font-semibold ${
                  activeTab === tt.value ? "bg-indigo-100 text-indigo-700" : "bg-gray-100 text-gray-500"
                }`}>
                  {count}
                </span>
              </button>
            );
          })}
        </div>

        <div className="p-4 space-y-2">
          {/* Preview toggle */}
          <div className="flex justify-end mb-2">
            <button
              type="button"
              onClick={() => setPreviewTab(previewTab === activeTab ? null : activeTab)}
              className={`rounded-lg px-3 py-1.5 text-xs font-medium transition-colors ${
                previewTab === activeTab
                  ? "bg-indigo-100 text-indigo-700"
                  : "border border-gray-300 text-gray-600 hover:bg-gray-50"
              }`}
            >
              {previewTab === activeTab ? "Close Preview" : "Preview"}
            </button>
          </div>

          {previewTab === activeTab ? (
            renderPreview(activeTab)
          ) : (
            <>
              {/* Question list */}
              {tabQuestions.length === 0 && !addingNew && (
                <p className="text-sm text-gray-400 py-2">No questions yet. Click "Add Question" to get started.</p>
              )}
              {tabQuestions
                .map((q, posInTab) => {
                  const globalIndex = questions.indexOf(q);
                  const isFirst = posInTab === 0;
                  const isLast = posInTab === tabQuestions.length - 1;

                  if (editingIndex === globalIndex) {
                    return (
                      <div key={globalIndex}>
                        {renderQuestionForm(saveEditedQuestion)}
                      </div>
                    );
                  }

                  return (
                    <div key={globalIndex} className="flex items-center gap-2 rounded-lg border border-gray-200 bg-white px-3 py-2 hover:border-gray-300 transition-colors">
                      {/* Drag handle / sort position */}
                      <div className="flex flex-col gap-0.5 shrink-0">
                        <button
                          type="button"
                          onClick={() => moveQuestion(globalIndex, "up")}
                          disabled={isFirst}
                          className="text-gray-400 hover:text-gray-600 disabled:opacity-30 disabled:cursor-not-allowed text-xs leading-none"
                          title="Move up"
                        >
                          &#9650;
                        </button>
                        <button
                          type="button"
                          onClick={() => moveQuestion(globalIndex, "down")}
                          disabled={isLast}
                          className="text-gray-400 hover:text-gray-600 disabled:opacity-30 disabled:cursor-not-allowed text-xs leading-none"
                          title="Move down"
                        >
                          &#9660;
                        </button>
                      </div>

                      {/* Content */}
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="text-sm font-medium text-gray-900">{q.section_name}</span>
                          {questionTypeBadge(q.question_type)}
                          {q.includes_rating && <span className="rounded-full bg-amber-50 px-2 py-0.5 text-[10px] font-semibold text-amber-600">Rating</span>}
                          {q.is_required && <span className="text-[10px] text-red-400 font-medium">*</span>}
                        </div>
                        {q.question_text && (
                          <p className="text-xs text-gray-500 mt-0.5 line-clamp-1">{q.question_text}</p>
                        )}
                      </div>

                      {/* Actions */}
                      <div className="flex gap-1 shrink-0">
                        <button
                          type="button"
                          onClick={() => startEdit(globalIndex)}
                          className="rounded border border-gray-200 px-2 py-1 text-xs text-gray-600 hover:bg-gray-50"
                        >
                          Edit
                        </button>
                        <button
                          type="button"
                          onClick={() => deleteQuestion(globalIndex)}
                          className="rounded border border-red-200 px-2 py-1 text-xs text-red-500 hover:bg-red-50"
                        >
                          Delete
                        </button>
                      </div>
                    </div>
                  );
                })}

              {/* Add new question form */}
              {addingNew && renderQuestionForm(saveNewQuestion)}

              {/* Add question button */}
              {!addingNew && (
                <button
                  type="button"
                  onClick={startAdd}
                  className="mt-2 w-full rounded-lg border-2 border-dashed border-gray-300 py-2.5 text-sm font-medium text-gray-500 hover:border-indigo-300 hover:text-indigo-600 transition-colors"
                >
                  + Add Question
                </button>
              )}
            </>
          )}
        </div>
      </div>

      {/* Save buttons */}
      <div className="flex items-center justify-between">
        <button type="button" onClick={onClose} className="rounded-lg border border-gray-300 px-4 py-2 text-sm text-gray-600 hover:bg-gray-50">
          Cancel
        </button>
        <div className="flex gap-2">
          <button
            type="button"
            onClick={() => void saveTemplate()}
            disabled={saving || !name.trim()}
            className="rounded-lg border border-indigo-300 bg-indigo-50 px-4 py-2 text-sm font-semibold text-indigo-700 hover:bg-indigo-100 disabled:opacity-50"
          >
            {saving ? "Saving..." : "Save"}
          </button>
          <button
            type="button"
            onClick={() => void saveAndClose()}
            disabled={saving || !name.trim()}
            className="rounded-lg bg-pme-blue px-4 py-2 text-sm font-semibold text-white disabled:opacity-60"
          >
            {saving ? "Saving..." : "Save & Close"}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ---------- Individual Reviews Tab ---------- */

function IndividualReviewsTab({
  cycle,
  selectedIds,
  setSelectedIds,
  onTriggerIndividual,
  onTriggerBulk,
  onBulkDates,
  onCompleteMeeting,
  onRefresh,
  statusBadge,
  colleagueStatus,
}: {
  cycle: ReviewCycle;
  selectedIds: Set<number>;
  setSelectedIds: (ids: Set<number>) => void;
  onTriggerIndividual: (cr: CycleReview) => void;
  onTriggerBulk: (crs: CycleReview[]) => void;
  onBulkDates: (ids: number[]) => void;
  onCompleteMeeting: (id: number) => void;
  onRefresh: () => void;
  statusBadge: (status: string) => string;
  colleagueStatus: Record<string, ColleagueStatus>;
}) {
  const [cycleEmployees, setCycleEmployees] = useState<CycleReview[]>([]);
  const [savedSnapshot, setSavedSnapshot] = useState<CycleReview[]>([]);
  const [loadingEmployees, setLoadingEmployees] = useState(false);
  const [sortField, setSortField] = useState<"name" | "department" | "status">("name");
  const [sortDir, setSortDir] = useState<"asc" | "desc">("asc");
  const [scheduling] = useState(false);
  const [sameDate, setSameDate] = useState("");
  const [showSameDate, setShowSameDate] = useState(false);
  const [showStaggerDropdown, setShowStaggerDropdown] = useState(false);
  const [staggerModalMode, setStaggerModalMode] = useState<"monthly" | "weekly" | "quarterly" | null>(null);
  const [colleagueModalReview, setColleagueModalReview] = useState<{ reviewId: number; employeeId: number; employeeName: string } | null>(null);
  const [matrixReviewId, setMatrixReviewId] = useState<number | null>(null);
  const [pendingDates, setPendingDates] = useState<Map<number, string>>(new Map());
  const [savingPending, setSavingPending] = useState(false);
  const [savingDraft, setSavingDraft] = useState(false);
  const [draftToast, setDraftToast] = useState("");
  const staggerDropdownRef = useRef<HTMLDivElement>(null);

  const refreshEmployees = useCallback(async () => {
    setLoadingEmployees(true);
    try {
      const data = await api.get<{ reviews: CycleReview[] }>(`/api/reviews/cycles/${cycle.id}/employees`);
      setCycleEmployees(data.reviews);
      setSavedSnapshot(data.reviews.map((r) => ({ ...r })));
    } catch { /* ignore */ }
    setLoadingEmployees(false);
  }, [cycle.id]);

  useEffect(() => {
    void refreshEmployees();
  }, [refreshEmployees]);

  // Close stagger dropdown on click outside
  useEffect(() => {
    if (!showStaggerDropdown) return;
    const handleClick = (e: MouseEvent) => {
      if (staggerDropdownRef.current && !staggerDropdownRef.current.contains(e.target as Node)) {
        setShowStaggerDropdown(false);
      }
    };
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, [showStaggerDropdown]);

  const toggleSort = (field: typeof sortField) => {
    if (sortField === field) setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    else { setSortField(field); setSortDir("asc"); }
  };

  const sorted = [...cycleEmployees].sort((a, b) => {
    const dir = sortDir === "asc" ? 1 : -1;
    if (sortField === "name") return a.employee_name.localeCompare(b.employee_name) * dir;
    if (sortField === "department") return (a.department_name || "").localeCompare(b.department_name || "") * dir;
    return a.status.localeCompare(b.status) * dir;
  });

  // Use employee_id as the key for selection since id can be null for unassigned employees
  const getRowKey = (cr: CycleReview) => cr.id ?? cr.employee_id;

  const toggleId = (cr: CycleReview) => {
    const key = getRowKey(cr);
    const next = new Set(selectedIds);
    if (next.has(key)) next.delete(key);
    else next.add(key);
    setSelectedIds(next);
  };

  const toggleAll = () => {
    if (selectedIds.size === sorted.length) setSelectedIds(new Set());
    else setSelectedIds(new Set(sorted.map((r) => getRowKey(r))));
  };

  const selectedReviews = sorted.filter((r) => selectedIds.has(getRowKey(r)));
  const canTriggerSelected = selectedReviews.some((r) => !r.triggered_at);
  const canSetDatesSelected = selectedReviews.filter((r) => r.id != null).length > 0;

  const today = new Date().toISOString().slice(0, 10);

  const isOverdue = (deadline: string | null | undefined) => {
    if (!deadline) return false;
    return deadline < today;
  };

  const hasReview = (cr: CycleReview) => cr.id != null;

  const getReviewCategory = (cr: CycleReview): "not_assigned" | "not_scheduled" | "scheduled" | "active" | "meeting" | "completed" => {
    if (!hasReview(cr)) return "not_assigned";
    if (cr.status === "completed" || cr.is_historical) return "completed";
    if (["meeting_pending", "employee_response_pending"].includes(cr.status)) return "meeting";
    if (cr.triggered_at) return "active";
    if (cr.scheduled_start_date) return "scheduled";
    return "not_scheduled";
  };

  const handleRefreshAndReload = () => {
    onRefresh();
    void refreshEmployees();
  };

  const addDaysToDate = (dateStr: string, days: number): string => {
    const d = new Date(dateStr);
    d.setDate(d.getDate() + days);
    return d.toISOString().slice(0, 10);
  };

  const updateLocalEmployee = (employeeId: number, patch: Partial<CycleReview>) => {
    setCycleEmployees((prev) =>
      prev.map((cr) => cr.employee_id === employeeId ? { ...cr, ...patch } : cr)
    );
  };

  const handleSetAllSameDate = () => {
    if (!sameDate || selectedReviews.length === 0) return;
    for (const cr of selectedReviews) {
      if (cr.triggered_at) continue;
      const selfDue = addDaysToDate(sameDate, cycle.deadline_self_days);
      const peerDue = addDaysToDate(sameDate, cycle.deadline_peer_days);
      const mgrDue = addDaysToDate(sameDate, cycle.deadline_manager_days);
      updateLocalEmployee(cr.employee_id, {
        scheduled_start_date: sameDate,
        individual_self_deadline: selfDue,
        individual_colleague_deadline: peerDue,
        individual_manager_deadline: mgrDue,
      });
    }
    setShowSameDate(false);
    setDraftToast("Due dates auto-calculated from start date");
    setTimeout(() => setDraftToast(""), 3000);
  };

  const handleInlineSchedule = (cr: CycleReview, dateValue: string) => {
    if (!dateValue) return;
    const selfDue = addDaysToDate(dateValue, cycle.deadline_self_days);
    const peerDue = addDaysToDate(dateValue, cycle.deadline_peer_days);
    const mgrDue = addDaysToDate(dateValue, cycle.deadline_manager_days);
    updateLocalEmployee(cr.employee_id, {
      scheduled_start_date: dateValue,
      individual_self_deadline: selfDue,
      individual_colleague_deadline: peerDue,
      individual_manager_deadline: mgrDue,
    });
    setDraftToast("Due dates recalculated");
    setTimeout(() => setDraftToast(""), 3000);
  };

  const handleInlineDeadlineChange = (_reviewId: number, field: string, value: string, employeeId: number) => {
    if (!value) return;
    const fieldMap: Record<string, string> = {
      self_deadline: "individual_self_deadline",
      colleague_deadline: "individual_colleague_deadline",
      manager_deadline: "individual_manager_deadline",
    };
    const key = fieldMap[field];
    if (key) {
      updateLocalEmployee(employeeId, { [key]: value } as Partial<CycleReview>);
    }
  };

  const handleSelectColleagues = async (cr: CycleReview) => {
    if (cr.id != null) {
      setColleagueModalReview({ reviewId: cr.id, employeeId: cr.employee_id, employeeName: cr.employee_name });
      return;
    }
    try {
      const result = await api.post<{ ok: boolean; review_id: number }>(`/api/reviews/cycles/${cycle.id}/schedule-individual`, {
        employee_id: cr.employee_id,
      });
      if (result.review_id) {
        await refreshEmployees();
        setColleagueModalReview({ reviewId: result.review_id, employeeId: cr.employee_id, employeeName: cr.employee_name });
      }
    } catch { /* ignore */ }
  };

  const handleStaggerApply = (dates: Map<number, string>) => {
    setPendingDates(dates);
    // Update local state so table reflects the pending dates
    setCycleEmployees((prev) =>
      prev.map((cr) => {
        const newDate = dates.get(cr.employee_id);
        if (newDate) return { ...cr, scheduled_start_date: newDate };
        return cr;
      })
    );
    setStaggerModalMode(null);
  };

  const handleSavePendingDates = async () => {
    if (pendingDates.size === 0) return;
    setSavingPending(true);
    for (const [employeeId, scheduledDate] of pendingDates) {
      try {
        await api.post(`/api/reviews/cycles/${cycle.id}/schedule-individual`, {
          employee_id: employeeId,
          scheduled_start_date: scheduledDate,
        });
      } catch { /* continue with remaining */ }
    }
    setPendingDates(new Map());
    setSavingPending(false);
    handleRefreshAndReload();
  };

  const hasDraftChanges = useMemo(() => {
    if (savedSnapshot.length === 0 && cycleEmployees.length === 0) return false;
    return cycleEmployees.some((cr) => {
      if (cr.triggered_at) return false;
      const saved = savedSnapshot.find((s) => s.employee_id === cr.employee_id);
      if (!saved) {
        return !!(cr.scheduled_start_date || cr.individual_self_deadline || cr.individual_colleague_deadline || cr.individual_manager_deadline || cr.auto_trigger_annually);
      }
      return (
        (cr.scheduled_start_date || "") !== (saved.scheduled_start_date || "") ||
        (cr.individual_self_deadline || "") !== (saved.individual_self_deadline || "") ||
        (cr.individual_colleague_deadline || "") !== (saved.individual_colleague_deadline || "") ||
        (cr.individual_manager_deadline || "") !== (saved.individual_manager_deadline || "") ||
        !!cr.auto_trigger_annually !== !!saved.auto_trigger_annually
      );
    });
  }, [cycleEmployees, savedSnapshot]);

  const handleSaveDraft = async () => {
    setSavingDraft(true);
    const updates = cycleEmployees
      .filter((cr) => {
        if (cr.triggered_at) return false;
        const saved = savedSnapshot.find((s) => s.employee_id === cr.employee_id);
        if (!saved) {
          return !!(cr.scheduled_start_date || cr.individual_self_deadline || cr.individual_colleague_deadline || cr.individual_manager_deadline || cr.auto_trigger_annually);
        }
        return (
          (cr.scheduled_start_date || "") !== (saved.scheduled_start_date || "") ||
          (cr.individual_self_deadline || "") !== (saved.individual_self_deadline || "") ||
          (cr.individual_colleague_deadline || "") !== (saved.individual_colleague_deadline || "") ||
          (cr.individual_manager_deadline || "") !== (saved.individual_manager_deadline || "") ||
          !!cr.auto_trigger_annually !== !!saved.auto_trigger_annually
        );
      })
      .map((cr) => ({
        review_id: cr.id,
        employee_id: cr.employee_id,
        scheduled_start_date: cr.scheduled_start_date || null,
        individual_self_deadline: cr.individual_self_deadline || null,
        individual_colleague_deadline: cr.individual_colleague_deadline || null,
        individual_manager_deadline: cr.individual_manager_deadline || null,
        auto_trigger_annually: !!cr.auto_trigger_annually,
      }));

    if (updates.length === 0) {
      setSavingDraft(false);
      return;
    }

    try {
      await api.put(`/api/reviews/cycles/${cycle.id}/bulk-update`, { updates });
      setPendingDates(new Map());
      setDraftToast("Changes saved successfully");
      setTimeout(() => setDraftToast(""), 3000);
      handleRefreshAndReload();
    } catch {
      setDraftToast("Failed to save changes");
      setTimeout(() => setDraftToast(""), 3000);
    }
    setSavingDraft(false);
  };

  const handleDiscardDraft = () => {
    setCycleEmployees(savedSnapshot.map((r) => ({ ...r })));
    setPendingDates(new Map());
  };

  const sortArrow = (field: typeof sortField) =>
    sortField === field ? (sortDir === "asc" ? " ↑" : " ↓") : "";

  // Determine eligible employees for stagger (selected if any, otherwise all un-triggered)
  const staggerEligible = useMemo(() => {
    const pool = selectedIds.size > 0
      ? sorted.filter((r) => selectedIds.has(getRowKey(r)))
      : sorted;
    return pool.filter((r) => !r.triggered_at);
  }, [sorted, selectedIds]);

  if (loadingEmployees && cycleEmployees.length === 0) {
    return <div className="px-4 pb-4 pt-3"><p className="text-sm text-gray-400">Loading employees...</p></div>;
  }

  return (
    <div className="px-4 pb-4 pt-3">
      {/* Bulk actions */}
      <div className="flex flex-wrap items-center gap-2 mb-3">
        <span className="text-xs text-gray-500">
          {selectedIds.size > 0 ? `${selectedIds.size} selected` : "Select reviews for bulk actions"}
        </span>
        <div className="flex gap-1 ml-auto">
          {selectedIds.size > 0 && canSetDatesSelected && (
            <button type="button" onClick={() => onBulkDates(Array.from(selectedIds).filter((id) => sorted.some((r) => r.id === id)))} className="rounded border border-gray-300 px-2 py-1 text-xs font-medium text-gray-700 hover:bg-gray-50" title="Set scheduled start and deadline dates for selected reviews">
              Set Dates
            </button>
          )}
          {selectedIds.size > 0 && canTriggerSelected && (
            <button type="button" onClick={() => onTriggerBulk(selectedReviews.filter((r) => !r.triggered_at))} className="rounded border border-green-300 px-2 py-1 text-xs font-medium text-green-700 hover:bg-green-50" title="Start reviews immediately for all selected employees">
              Trigger Selected
            </button>
          )}
          <div className="relative" ref={staggerDropdownRef}>
            <button
              type="button"
              onClick={() => setShowStaggerDropdown(!showStaggerDropdown)}
              className="rounded border border-purple-300 px-2 py-1 text-xs font-medium text-purple-700 hover:bg-purple-50 flex items-center gap-1"
              title="Distribute review start dates evenly across weekly, monthly, or quarterly intervals"
            >
              Stagger
              <svg className="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" /></svg>
            </button>
            {showStaggerDropdown && (
              <div className="absolute right-0 mt-1 w-44 rounded-lg border border-gray-200 bg-white shadow-lg z-20">
                <button
                  type="button"
                  onClick={() => { setStaggerModalMode("weekly"); setShowStaggerDropdown(false); }}
                  className="block w-full px-3 py-2 text-left text-xs text-gray-700 hover:bg-purple-50 rounded-t-lg"
                >
                  Stagger Weekly
                </button>
                <button
                  type="button"
                  onClick={() => { setStaggerModalMode("monthly"); setShowStaggerDropdown(false); }}
                  className="block w-full px-3 py-2 text-left text-xs text-gray-700 hover:bg-purple-50"
                >
                  Stagger Monthly
                </button>
                <button
                  type="button"
                  onClick={() => { setStaggerModalMode("quarterly"); setShowStaggerDropdown(false); }}
                  className="block w-full px-3 py-2 text-left text-xs text-gray-700 hover:bg-purple-50 rounded-b-lg"
                >
                  Stagger Quarterly
                </button>
              </div>
            )}
          </div>
          <button type="button" onClick={() => setShowSameDate(!showSameDate)} className="rounded border border-blue-300 px-2 py-1 text-xs font-medium text-blue-700 hover:bg-blue-50" title="Set the same scheduled start date for all selected reviews">
            Set All Same Date
          </button>
        </div>
      </div>

      {/* Pending dates banner */}
      {pendingDates.size > 0 && (
        <div className="mb-3 rounded-lg border border-amber-300 bg-amber-50 p-3 flex items-center gap-3">
          <span className="text-xs font-medium text-amber-800">
            {pendingDates.size} unsaved date {pendingDates.size === 1 ? "change" : "changes"}
          </span>
          <div className="flex gap-2 ml-auto">
            <button
              type="button"
              onClick={() => { setPendingDates(new Map()); void refreshEmployees(); }}
              className="rounded border border-gray-300 px-2 py-1 text-xs font-medium text-gray-600 hover:bg-gray-50"
            >
              Discard
            </button>
            <button
              type="button"
              onClick={() => void handleSavePendingDates()}
              disabled={savingPending}
              className="rounded bg-amber-600 px-3 py-1 text-xs font-semibold text-white hover:bg-amber-700 disabled:opacity-50"
            >
              {savingPending ? "Saving..." : "Save All Dates"}
            </button>
          </div>
        </div>
      )}

      {/* Same date form */}
      {showSameDate && (
        <div className="mb-3 rounded-lg border border-blue-200 bg-blue-50/50 p-3 flex items-end gap-3">
          <label className="block text-xs font-medium text-gray-700">
            Scheduled date
            <input type="date" value={sameDate} onChange={(e) => setSameDate(e.target.value)} className="input mt-1 w-40" />
          </label>
          <p className="text-[11px] text-gray-500 flex-1">
            {selectedIds.size > 0
              ? `Schedule ${selectedReviews.filter((r) => !r.triggered_at && hasReview(r)).length} un-triggered reviews to the same date`
              : "Select reviews first, then set date"}
          </p>
          <button type="button" onClick={() => void handleSetAllSameDate()} disabled={scheduling || !sameDate || selectedIds.size === 0} className="rounded bg-blue-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-blue-700 disabled:opacity-50">
            {scheduling ? "Scheduling..." : "Apply Date"}
          </button>
          <button type="button" onClick={() => setShowSameDate(false)} className="text-xs text-gray-400 hover:text-gray-600">Cancel</button>
        </div>
      )}

      {/* Table */}
      <div className="overflow-x-auto">
        <table className="w-full text-xs">
          <thead>
            <tr className="border-b border-gray-200 text-left">
              <th className="px-2 py-2 w-8" title="Select all rows">
                <input type="checkbox" checked={selectedIds.size === sorted.length && sorted.length > 0} onChange={toggleAll} className="h-3.5 w-3.5 rounded border-gray-300" title="Select / deselect all" />
              </th>
              <th className="px-2 py-2 cursor-pointer hover:text-pme-blue" onClick={() => toggleSort("name")} title="Employee name — click to sort">Employee{sortArrow("name")}</th>
              <th className="px-2 py-2 cursor-pointer hover:text-pme-blue" onClick={() => toggleSort("department")} title="Department — click to sort">Department{sortArrow("department")}</th>
              <th className="px-2 py-2" title="Scheduled start date for this review">Scheduled Start</th>
              <th className="px-2 py-2" title="Self-assessment deadline">Self Due</th>
              <th className="px-2 py-2" title="Colleague feedback deadline">Colleague Due</th>
              <th className="px-2 py-2" title="Manager review deadline">Manager Due</th>
              <th className="px-2 py-2" title="Number of colleagues asked for feedback">Peers</th>
              <th className="px-2 py-2" title="Auto-trigger annually — create a new review each period">Auto</th>
              <th className="px-2 py-2 cursor-pointer hover:text-pme-blue" onClick={() => toggleSort("status")} title="Review status — click to sort">Status{sortArrow("status")}</th>
              <th className="px-2 py-2 text-right" title="Available actions for this review">Actions</th>
            </tr>
          </thead>
          <tbody>
            {sorted.map((cr) => {
              const cat = getReviewCategory(cr);
              const rowKey = getRowKey(cr);
              return (
                <tr key={`${cr.employee_id}-${cr.id ?? "na"}`} className={`border-b border-gray-100 hover:bg-gray-50 ${selectedIds.has(rowKey) ? "bg-blue-50/30" : ""}`}>
                  <td className="px-2 py-2">
                    <input type="checkbox" checked={selectedIds.has(rowKey)} onChange={() => toggleId(cr)} className="h-3.5 w-3.5 rounded border-gray-300" />
                  </td>
                  <td className="px-2 py-2 font-medium text-gray-900">{cr.employee_name}</td>
                  <td className="px-2 py-2 text-gray-500">{cr.department_name || "–"}</td>
                  <td className="px-2 py-2">
                    {cat === "active" || cat === "meeting" || cat === "completed" ? (
                      <span className="text-gray-500">{cr.scheduled_start_date || "–"}</span>
                    ) : (
                      <input
                        type="date"
                        value={cr.scheduled_start_date || ""}
                        onChange={(e) => handleInlineSchedule(cr, e.target.value)}
                        className="rounded border border-gray-200 px-1.5 py-0.5 text-xs w-32"
                        title="Set scheduled start date — due dates auto-calculated"
                      />
                    )}
                  </td>
                  <td className="px-2 py-2">
                    {cr.triggered_at ? (
                      <span className={isOverdue(cr.self_assessment_deadline) ? "text-red-600 font-semibold" : "text-gray-500"}>
                        {cr.self_assessment_deadline || "–"}
                      </span>
                    ) : (
                      <input
                        type="date"
                        value={cr.individual_self_deadline || ""}
                        onChange={(e) => handleInlineDeadlineChange(cr.id ?? 0, "self_deadline", e.target.value, cr.employee_id)}
                        className="rounded border border-gray-200 px-1.5 py-0.5 text-xs w-32"
                        title="Self-assessment deadline"
                      />
                    )}
                  </td>
                  <td className="px-2 py-2">
                    {cr.triggered_at ? (
                      <span className={isOverdue(cr.colleague_feedback_deadline) ? "text-red-600 font-semibold" : "text-gray-500"}>
                        {cr.colleague_feedback_deadline || "–"}
                      </span>
                    ) : (
                      <input
                        type="date"
                        value={cr.individual_colleague_deadline || ""}
                        onChange={(e) => handleInlineDeadlineChange(cr.id ?? 0, "colleague_deadline", e.target.value, cr.employee_id)}
                        className="rounded border border-gray-200 px-1.5 py-0.5 text-xs w-32"
                        title="Colleague feedback deadline"
                      />
                    )}
                  </td>
                  <td className="px-2 py-2">
                    {cr.triggered_at ? (
                      <span className={isOverdue(cr.manager_review_deadline) ? "text-red-600 font-semibold" : "text-gray-500"}>
                        {cr.manager_review_deadline || "–"}
                      </span>
                    ) : (
                      <input
                        type="date"
                        value={cr.individual_manager_deadline || ""}
                        onChange={(e) => handleInlineDeadlineChange(cr.id ?? 0, "manager_deadline", e.target.value, cr.employee_id)}
                        className="rounded border border-gray-200 px-1.5 py-0.5 text-xs w-32"
                        title="Manager review deadline"
                      />
                    )}
                  </td>
                  <td className="px-2 py-2">
                    {(() => {
                      const cs = cr.id != null ? colleagueStatus[String(cr.id)] : undefined;
                      const total = cs ? cs.submitted.length + cs.pending.length : (cr.colleague_count ?? 0);
                      const blocking = cs ? cs.pending.filter((p) => !p.expired) : [];
                      const overdue = cs ? cs.pending.filter((p) => p.expired) : [];
                      return (
                        <>
                          <button
                            type="button"
                            onClick={() => void handleSelectColleagues(cr)}
                            className="text-[10px] font-medium text-purple-700 hover:text-purple-900 hover:underline"
                            title="Choose or manage the colleagues giving feedback on this employee"
                          >
                            {total > 0
                              ? (cs ? `${cs.submitted.length}/${total} submitted` : `${total} peers`)
                              : "Select"}
                          </button>
                          {cs && cs.submitted.length > 0 && cr.id != null && (
                            <button
                              type="button"
                              onClick={() => setMatrixReviewId(cr.id!)}
                              className="mt-0.5 block text-[10px] font-medium text-purple-700 hover:underline"
                              title="View and export the colleague feedback comparison"
                            >
                              Colleague Feedback
                            </button>
                          )}
                          {blocking.length > 0 && (
                            <p className="mt-0.5 text-[10px] text-amber-700" title="These peers have not yet submitted and still block the manager review">
                              Waiting: {blocking.map((p) => p.name).join(", ")}
                            </p>
                          )}
                          {overdue.length > 0 && (
                            <p className="mt-0.5 text-[10px] text-gray-400" title="Deadline passed — no longer blocks the manager review">
                              Overdue: {overdue.map((p) => p.name).join(", ")}
                            </p>
                          )}
                        </>
                      );
                    })()}
                  </td>
                  <td className="px-2 py-2 text-center">
                    <input
                      type="checkbox"
                      checked={!!cr.auto_trigger_annually}
                      onChange={() => updateLocalEmployee(cr.employee_id, { auto_trigger_annually: !cr.auto_trigger_annually })}
                      className="h-3.5 w-3.5 rounded border-gray-300"
                      title="Auto-trigger annually — when enabled, a new review is created at the start of each period"
                    />
                  </td>
                  <td className="px-2 py-2">
                    <span className={`inline-block rounded-full px-2 py-0.5 text-[10px] font-semibold ${
                      cat === "not_assigned" ? "bg-gray-100 text-gray-400"
                      : cat === "not_scheduled" ? "bg-gray-100 text-gray-500"
                      : cat === "scheduled" ? "bg-purple-50 text-purple-700"
                      : cat === "completed" ? "bg-green-50 text-green-700"
                      : cat === "meeting" ? "bg-amber-50 text-amber-700"
                      : statusBadge(cr.status)
                    }`}>
                      {cat === "not_assigned" ? "Not assigned"
                        : cat === "not_scheduled" ? "Not scheduled"
                        : cat === "scheduled" ? `Scheduled ${cr.scheduled_start_date || ""}`
                        : reviewStatusLabel(cr.status)}
                    </span>
                  </td>
                  <td className="px-2 py-2 text-right">
                    <div className="flex gap-1 justify-end">
                      {(cat === "not_assigned" || cat === "not_scheduled") && (
                        <button type="button" onClick={() => onTriggerIndividual(cr)} className="rounded border border-green-300 px-1.5 py-0.5 text-[10px] font-medium text-green-700 hover:bg-green-50" title="Start this employee's review process immediately">
                          Trigger
                        </button>
                      )}
                      {cat === "scheduled" && (
                        <button type="button" onClick={() => onTriggerIndividual(cr)} className="rounded border border-green-300 px-1.5 py-0.5 text-[10px] font-medium text-green-700 hover:bg-green-50" title="Start this review now instead of waiting for the scheduled date">
                          Trigger Now
                        </button>
                      )}
                      {/* A review that exists is openable by an admin at ANY
                          status — read-only where the status does not permit
                          editing. Previously 'meeting' offered only Complete,
                          so an in-flight review could not be inspected. */}
                      {cr.id != null && (
                        <Link
                          to={`/review?review=${cr.id}&admin=1`}
                          className="rounded border border-blue-300 px-1.5 py-0.5 text-[10px] font-medium text-blue-700 hover:bg-blue-50"
                          title="Open the full review (admin view — includes manager and confidential sections)"
                        >
                          View
                        </Link>
                      )}
                      {cr.id != null && !["not_assigned", "not_scheduled", "scheduled"].includes(cat)
                        && (cr.status === "not_started" || cr.status === "self_assessment_in_progress") && (
                        <button
                          type="button"
                          onClick={async () => {
                            if (cr.id == null) return;
                            try {
                              const res = await api.post<{ message: string }>(`/api/reviews/${cr.id}/resend-invite`);
                              setDraftToast(res.message);
                            } catch (err) {
                              setDraftToast(err instanceof ApiError ? err.message : "Couldn't resend the invitation");
                            }
                            setTimeout(() => setDraftToast(""), 5000);
                          }}
                          className="rounded border border-gray-300 px-1.5 py-0.5 text-[10px] font-medium text-gray-700 hover:bg-gray-50"
                          title="Email the appraisal invitation again, with a fresh sign-in link"
                        >
                          Resend invite
                        </button>
                      )}
                      {cat === "meeting" && cr.id != null && (
                        <button type="button" onClick={() => { if (cr.id != null) onCompleteMeeting(cr.id); }} className="rounded border border-green-400 bg-green-50 px-1.5 py-0.5 text-[10px] font-medium text-green-700 hover:bg-green-100" title="Complete the review meeting and finalise">
                          Complete
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}
            {sorted.length === 0 && (
              <tr><td colSpan={11} className="px-2 py-6 text-center text-gray-400 text-sm">No employees in this cycle&apos;s scope. Check the cycle scope settings.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Draft toast */}
      {draftToast && (
        <div className="fixed bottom-20 left-1/2 -translate-x-1/2 z-50 rounded-lg bg-gray-800 px-4 py-2 text-sm text-white shadow-lg">
          {draftToast}
        </div>
      )}

      {/* Unsaved changes sticky footer */}
      {(hasDraftChanges || pendingDates.size > 0) && (
        <div className="sticky bottom-0 left-0 right-0 -mx-4 mt-4 border-t border-blue-200 bg-blue-50 px-4 py-3 flex items-center gap-3 shadow-lg z-10">
          <span className="text-sm font-medium text-blue-800 flex-1">
            You have unsaved changes
          </span>
          <button
            type="button"
            onClick={handleDiscardDraft}
            className="rounded border border-gray-300 bg-white px-3 py-1.5 text-xs font-medium text-gray-600 hover:bg-gray-50"
          >
            Discard
          </button>
          <button
            type="button"
            onClick={() => void handleSaveDraft()}
            disabled={savingDraft}
            className="rounded bg-blue-600 px-4 py-1.5 text-xs font-semibold text-white hover:bg-blue-700 disabled:opacity-50"
          >
            {savingDraft ? "Saving..." : "Save Changes"}
          </button>
        </div>
      )}

      {/* Stagger modal */}
      {staggerModalMode && (
        <StaggerModal
          mode={staggerModalMode}
          employees={staggerEligible}
          onApply={handleStaggerApply}
          onClose={() => setStaggerModalMode(null)}
        />
      )}

      {/* Colleague selection modal */}
      {matrixReviewId !== null && (
        <ColleagueFeedbackMatrix
          reviewId={matrixReviewId}
          onClose={() => setMatrixReviewId(null)}
        />
      )}

      {colleagueModalReview && (
        <ColleagueModal
          reviewId={colleagueModalReview.reviewId}
          employeeId={colleagueModalReview.employeeId}
          employeeName={colleagueModalReview.employeeName}
          cycleId={cycle.id}
          onClose={() => setColleagueModalReview(null)}
          onSaved={() => handleRefreshAndReload()}
        />
      )}
    </div>
  );
}

/* ---------- Stagger Modal ---------- */

type StaggerMode = "monthly" | "weekly" | "quarterly";

const STAGGER_INTERVALS: Record<StaggerMode, { days: number; periods: number; label: string }> = {
  weekly: { days: 7, periods: 52, label: "Weekly" },
  monthly: { days: 30, periods: 12, label: "Monthly" },
  quarterly: { days: 90, periods: 4, label: "Quarterly" },
};

function StaggerModal({
  mode,
  employees,
  onApply,
  onClose,
}: {
  mode: StaggerMode;
  employees: CycleReview[];
  onApply: (dates: Map<number, string>) => void;
  onClose: () => void;
}) {
  const todayStr = new Date().toISOString().slice(0, 10);
  const [startDate, setStartDate] = useState(todayStr);
  const config = STAGGER_INTERVALS[mode];
  const defaultBatchSize = Math.max(1, Math.ceil(employees.length / config.periods));
  const [batchSize, setBatchSize] = useState(defaultBatchSize);
  const [sortBy, setSortBy] = useState<"alphabetical" | "department">("alphabetical");

  interface StaggerPreviewRow { employee_id: number; employee_name: string; department_name: string | null; scheduled_date: string }

  const preview = useMemo((): StaggerPreviewRow[] => {
    if (!startDate || employees.length === 0) return [];

    const sortedEmps = [...employees].sort((a, b) => {
      if (sortBy === "department") {
        const deptCmp = (a.department_name || "").localeCompare(b.department_name || "");
        if (deptCmp !== 0) return deptCmp;
      }
      return a.employee_name.localeCompare(b.employee_name);
    });

    const result: StaggerPreviewRow[] = [];
    const start = new Date(startDate);
    const size = Math.max(1, batchSize);

    for (let i = 0; i < sortedEmps.length; i++) {
      const batchIndex = Math.floor(i / size);
      const schedDate = new Date(start);
      schedDate.setDate(schedDate.getDate() + batchIndex * config.days);
      result.push({
        employee_id: sortedEmps[i].employee_id,
        employee_name: sortedEmps[i].employee_name,
        department_name: sortedEmps[i].department_name || null,
        scheduled_date: schedDate.toISOString().slice(0, 10),
      });
    }
    return result;
  }, [startDate, employees, batchSize, sortBy, config.days]);

  const handleApply = () => {
    const dates = new Map<number, string>();
    for (const row of preview) {
      dates.set(row.employee_id, row.scheduled_date);
    }
    onApply(dates);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" onClick={onClose}>
      <div className="mx-4 w-full max-w-2xl rounded-xl bg-white shadow-xl flex flex-col max-h-[90vh]" onClick={(e) => e.stopPropagation()}>
        <div className="p-5 border-b border-gray-100">
          <h3 className="text-sm font-semibold text-gray-900">Stagger {config.label}</h3>
          <p className="text-xs text-gray-500 mt-1">
            Schedule {employees.length} eligible {employees.length === 1 ? "review" : "reviews"} across {config.label.toLowerCase()} intervals ({config.days}-day gaps)
          </p>
        </div>

        <div className="p-5 space-y-4">
          <div className="flex flex-wrap gap-4">
            <label className="block text-xs font-medium text-gray-700">
              Starting from
              <input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                className="input mt-1 w-40 block"
              />
            </label>
            <label className="block text-xs font-medium text-gray-700">
              Employees per batch
              <input
                type="number"
                min={1}
                max={employees.length || 1}
                value={batchSize}
                onChange={(e) => setBatchSize(Math.max(1, parseInt(e.target.value) || 1))}
                className="input mt-1 w-24 block"
              />
            </label>
            <div className="text-xs font-medium text-gray-700">
              Sort by
              <div className="mt-1 flex rounded-lg border border-gray-200 overflow-hidden">
                <button
                  type="button"
                  onClick={() => setSortBy("alphabetical")}
                  className={`px-3 py-1.5 text-xs ${sortBy === "alphabetical" ? "bg-purple-100 text-purple-700 font-semibold" : "text-gray-600 hover:bg-gray-50"}`}
                >
                  Alphabetical
                </button>
                <button
                  type="button"
                  onClick={() => setSortBy("department")}
                  className={`px-3 py-1.5 text-xs border-l border-gray-200 ${sortBy === "department" ? "bg-purple-100 text-purple-700 font-semibold" : "text-gray-600 hover:bg-gray-50"}`}
                >
                  Department
                </button>
              </div>
            </div>
          </div>

          {/* Preview table */}
          {employees.length === 0 ? (
            <p className="text-xs text-gray-400 text-center py-4">No eligible employees. Select employees or ensure there are un-triggered reviews.</p>
          ) : (
            <div className="overflow-y-auto max-h-64 border border-gray-200 rounded-lg">
              <table className="w-full text-xs">
                <thead className="sticky top-0 bg-gray-50">
                  <tr className="border-b border-gray-200 text-left">
                    <th className="px-3 py-2 font-medium text-gray-600">Employee</th>
                    <th className="px-3 py-2 font-medium text-gray-600">Department</th>
                    <th className="px-3 py-2 font-medium text-gray-600">Scheduled Date</th>
                  </tr>
                </thead>
                <tbody>
                  {preview.map((row) => (
                    <tr key={row.employee_id} className="border-b border-gray-50">
                      <td className="px-3 py-1.5 text-gray-900">{row.employee_name}</td>
                      <td className="px-3 py-1.5 text-gray-500">{row.department_name || "–"}</td>
                      <td className="px-3 py-1.5 text-purple-700 font-medium">{row.scheduled_date}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        <div className="p-5 border-t border-gray-100 flex gap-2 justify-end">
          <button type="button" onClick={onClose} className="rounded border border-gray-300 px-3 py-1.5 text-xs font-medium text-gray-600 hover:bg-gray-50">
            Cancel
          </button>
          <button
            type="button"
            onClick={handleApply}
            disabled={preview.length === 0 || !startDate}
            className="rounded bg-purple-600 px-4 py-1.5 text-xs font-semibold text-white hover:bg-purple-700 disabled:opacity-50"
          >
            Apply
          </button>
        </div>
      </div>
    </div>
  );
}

/* ---------- Colleague Selection Modal ---------- */

function ColleagueModal({
  reviewId,
  employeeId,
  employeeName,
  cycleId,
  onClose,
  onSaved,
}: {
  reviewId: number;
  employeeId: number;
  employeeName: string;
  cycleId: number;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [colleagues, setColleagues] = useState<ColleagueFeedbackItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [removing, setRemoving] = useState<number | null>(null);
  const [saving, setSaving] = useState(false);
  const [showFocusAreas, setShowFocusAreas] = useState(false);
  const [focusAreas, setFocusAreas] = useState<string[]>([]);
  const [savingFocusAreas, setSavingFocusAreas] = useState(false);

  const [stagedIds, setStagedIds] = useState<number[]>([]);

  const loadColleagues = useCallback(async () => {
    try {
      const data = await api.get<{ colleagues: ColleagueFeedbackItem[]; focus_areas: string[] | null }>(`/api/reviews/${reviewId}/colleagues`);
      setColleagues(data.colleagues);
      if (data.focus_areas && data.focus_areas.length > 0) {
        setFocusAreas(data.focus_areas);
        setShowFocusAreas(true);
      }
    } catch { /* ignore */ }
    setLoading(false);
  }, [reviewId]);

  useEffect(() => {
    void loadColleagues();
  }, [loadColleagues]);

  const excludeIds = useMemo(() => {
    const ids = [employeeId];
    for (const c of colleagues) ids.push(c.colleague_id);
    return ids;
  }, [employeeId, colleagues]);

  const handleMultiChange = (ids: number[]) => {
    setStagedIds(ids);
  };

  const addFocusArea = () => {
    setFocusAreas((prev) => [...prev, ""]);
    setShowFocusAreas(true);
  };

  const updateFocusAreaAt = (index: number, value: string) => {
    setFocusAreas((prev) => prev.map((fa, i) => i === index ? value : fa));
  };

  const removeFocusAreaAt = (index: number) => {
    setFocusAreas((prev) => prev.filter((_, i) => i !== index));
  };

  const saveFocusAreas = async () => {
    setSavingFocusAreas(true);
    try {
      const filtered = focusAreas.filter((fa) => fa.trim());
      await api.put(`/api/reviews/cycles/${cycleId}/reviews/${reviewId}/focus-areas`, {
        focus_areas: filtered.length > 0 ? filtered : null,
      });
      setFocusAreas(filtered);
    } catch { /* ignore */ }
    setSavingFocusAreas(false);
  };

  const handleSaveAll = async () => {
    if (stagedIds.length === 0) return;
    setSaving(true);
    const fa = focusAreas.filter((f) => f.trim()).join("; ");
    for (const colId of stagedIds) {
      try {
        await api.post(`/api/reviews/${reviewId}/colleagues`, {
          colleague_id: colId,
          focus_area: fa,
        });
      } catch { /* continue */ }
    }
    setStagedIds([]);
    await loadColleagues();
    onSaved();
    setSaving(false);
  };

  const handleRemove = async (feedbackId: number) => {
    setRemoving(feedbackId);
    try {
      await api.delete(`/api/reviews/colleagues/${feedbackId}`);
      await loadColleagues();
      onSaved();
    } catch { /* ignore */ }
    setRemoving(null);
  };

  const totalCount = colleagues.length + stagedIds.length;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" onClick={onClose}>
      <div
        className="mx-4 rounded-xl bg-white shadow-xl flex flex-col"
        style={{ width: "max(600px, 50vw)", minHeight: "500px", maxHeight: "80vh" }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="p-6 border-b border-gray-100">
          <h3 className="text-base font-bold text-gray-900">
            Choose colleagues to give feedback
          </h3>
          <p className="text-sm text-gray-500 mt-1">
            {employeeName}
          </p>
        </div>

        <div className="flex-1 overflow-y-auto p-6 space-y-5">
          {/* Focus areas section — collapsible */}
          <div className="rounded-lg border border-gray-200">
            <button
              type="button"
              onClick={() => setShowFocusAreas(!showFocusAreas)}
              className="flex w-full items-center justify-between px-4 py-3 text-left"
            >
              <span className="text-sm font-medium text-gray-700">
                Focus areas {focusAreas.filter((fa) => fa.trim()).length > 0 && (
                  <span className="ml-1 text-xs text-purple-600">({focusAreas.filter((fa) => fa.trim()).length})</span>
                )}
              </span>
              <span className="text-xs text-gray-400">{showFocusAreas ? "▴" : "▾"}</span>
            </button>
            {showFocusAreas && (
              <div className="border-t border-gray-100 px-4 py-3 space-y-2">
                <p className="text-xs text-gray-500">Focus areas apply to all colleagues equally. They are included in feedback request emails and shown on the feedback form.</p>
                {focusAreas.map((fa, i) => (
                  <div key={i} className="flex items-center gap-2">
                    <input
                      type="text"
                      value={fa}
                      onChange={(e) => updateFocusAreaAt(i, e.target.value)}
                      onBlur={() => void saveFocusAreas()}
                      placeholder="e.g. Communication skills, Project delivery..."
                      className="flex-1 rounded border border-gray-200 px-3 py-1.5 text-sm focus:border-purple-400 focus:outline-none focus:ring-1 focus:ring-purple-400"
                    />
                    <button
                      type="button"
                      onClick={() => { removeFocusAreaAt(i); void saveFocusAreas(); }}
                      className="text-red-400 hover:text-red-600 p-1"
                    >
                      <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
                    </button>
                  </div>
                ))}
                <button
                  type="button"
                  onClick={addFocusArea}
                  disabled={savingFocusAreas}
                  className="flex items-center gap-1 text-xs font-medium text-purple-600 hover:text-purple-800"
                >
                  <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" /></svg>
                  Add Focus Area
                </button>
              </div>
            )}
          </div>

          {/* Previously assigned colleagues */}
          {loading ? (
            <p className="text-sm text-gray-400">Loading colleagues...</p>
          ) : colleagues.length > 0 && (
            <div className="space-y-2">
              <p className="text-sm font-medium text-gray-600">Previously selected ({colleagues.length})</p>
              <div className="space-y-2">
                {colleagues.map((c) => (
                  <div key={c.id} className="flex items-center gap-3 rounded-lg border border-gray-200 bg-white px-4 py-3">
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-900">{c.colleague_name}</p>
                      {c.focus_area && <p className="text-xs text-gray-500 mt-0.5">{c.focus_area}</p>}
                    </div>
                    <span className={`inline-block rounded-full px-2 py-0.5 text-[11px] font-semibold ${
                      c.status === "completed" || c.status === "submitted" ? "bg-green-50 text-green-700"
                      : c.status === "pending" ? "bg-gray-100 text-gray-500"
                      : "bg-blue-50 text-blue-700"
                    }`}>
                      {c.status === "completed" || c.status === "submitted" ? "Submitted" : "Pending"}
                    </span>
                    {c.status !== "submitted" && c.status !== "completed" && c.invited_at && (
                      <ResendColleagueButton feedbackId={c.id} name={c.colleague_name} />
                    )}
                    {c.status === "pending" && (
                      <button
                        type="button"
                        onClick={() => void handleRemove(c.id)}
                        disabled={removing === c.id}
                        className="text-red-400 hover:text-red-600 disabled:opacity-50 p-1"
                        title="Remove this peer reviewer"
                      >
                        <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
                      </button>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Search and add colleagues */}
          <div className="space-y-2">
            <p className="text-sm font-medium text-gray-600">Add peer reviewers</p>
            <EmployeeSearchMulti
              values={stagedIds}
              onChange={handleMultiChange}
              placeholder="Search and select employees..."
              className="input"
              excludeIds={excludeIds}
            />
          </div>
        </div>

        {/* Footer */}
        <div className="p-6 border-t border-gray-100 flex items-center gap-3">
          <span className="text-sm text-gray-500 flex-1">
            {totalCount} colleague{totalCount !== 1 ? "s" : ""} selected
          </span>
          <button type="button" onClick={onClose} className="rounded border border-gray-300 px-4 py-2 text-sm font-medium text-gray-600 hover:bg-gray-50">
            Cancel
          </button>
          {stagedIds.length > 0 && (
            <button
              type="button"
              onClick={() => void handleSaveAll()}
              disabled={saving}
              className="rounded bg-purple-600 px-5 py-2 text-sm font-semibold text-white hover:bg-purple-700 disabled:opacity-50"
            >
              {saving ? "Saving..." : "Save"}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

/* ---------- Individual Trigger Modal ---------- */

function IndividualTriggerModal({
  review,
  cycleId,
  onClose,
  onTriggered,
}: {
  review: CycleReview;
  cycleId: number;
  onClose: () => void;
  onTriggered: () => void;
}) {
  const [selfDeadline, setSelfDeadline] = useState(review.individual_self_deadline || "");
  const [colleagueDeadline, setColleagueDeadline] = useState(review.individual_colleague_deadline || "");
  const [managerDeadline, setManagerDeadline] = useState(review.individual_manager_deadline || "");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  const handleSubmit = async () => {
    setSubmitting(true);
    setError("");
    try {
      await api.post(`/api/reviews/cycles/${cycleId}/trigger-individual`, {
        employee_id: review.employee_id,
        self_deadline: selfDeadline || undefined,
        colleague_deadline: colleagueDeadline || undefined,
        manager_deadline: managerDeadline || undefined,
      });
      onTriggered();
    } catch (err) {
      if (err instanceof ApiError) setError(err.message);
      else setError("Failed to trigger review");
    }
    setSubmitting(false);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" onClick={onClose}>
      <div className="mx-4 w-full max-w-md rounded-xl bg-white p-6 shadow-xl" onClick={(e) => e.stopPropagation()}>
        <h3 className="text-sm font-semibold text-gray-900 mb-1">Trigger Review</h3>
        <p className="text-xs text-gray-500 mb-4">Start the review process for <span className="font-semibold text-gray-800">{review.employee_name}</span></p>
        {error && <p className="mb-3 rounded-lg bg-red-50 px-3 py-2 text-sm text-red-600">{error}</p>}

        <div className="space-y-3">
          <label className="block text-xs font-medium text-gray-700">
            Self-Assessment Deadline
            <input type="date" value={selfDeadline} onChange={(e) => setSelfDeadline(e.target.value)} className="input mt-1 w-full" placeholder="Uses cycle default" />
          </label>
          <label className="block text-xs font-medium text-gray-700">
            Colleague Feedback Deadline
            <input type="date" value={colleagueDeadline} onChange={(e) => setColleagueDeadline(e.target.value)} className="input mt-1 w-full" />
          </label>
          <label className="block text-xs font-medium text-gray-700">
            Manager Review Deadline
            <input type="date" value={managerDeadline} onChange={(e) => setManagerDeadline(e.target.value)} className="input mt-1 w-full" />
          </label>
          <p className="text-[11px] text-gray-400">Leave blank to use cycle defaults.</p>
        </div>

        <div className="mt-5 flex gap-2 justify-end">
          <button type="button" onClick={onClose} className="rounded-lg border border-gray-300 px-4 py-2 text-sm">Cancel</button>
          <button type="button" onClick={() => void handleSubmit()} disabled={submitting} className="rounded-lg bg-green-600 px-4 py-2 text-sm font-semibold text-white hover:bg-green-700 disabled:opacity-50">
            {submitting ? "Triggering..." : "Trigger Review"}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ---------- Bulk Trigger Modal ---------- */

function BulkTriggerModal({
  reviews,
  cycleId,
  onClose,
  onTriggered,
}: {
  reviews: CycleReview[];
  cycleId: number;
  onClose: () => void;
  onTriggered: () => void;
}) {
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [progress, setProgress] = useState(0);

  const handleSubmit = async () => {
    setSubmitting(true);
    setError("");
    let done = 0;
    for (const cr of reviews) {
      try {
        await api.post(`/api/reviews/cycles/${cycleId}/trigger-individual`, {
          employee_id: cr.employee_id,
        });
      } catch { /* continue with remaining */ }
      done++;
      setProgress(done);
    }
    setSubmitting(false);
    onTriggered();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" onClick={onClose}>
      <div className="mx-4 w-full max-w-lg rounded-xl bg-white p-6 shadow-xl" onClick={(e) => e.stopPropagation()}>
        <h3 className="text-sm font-semibold text-gray-900 mb-3">Trigger {reviews.length} Review{reviews.length !== 1 ? "s" : ""}</h3>
        {error && <p className="mb-3 rounded-lg bg-red-50 px-3 py-2 text-sm text-red-600">{error}</p>}

        <p className="text-xs text-gray-600 mb-2">The following employees will have their reviews started immediately with cycle default deadlines:</p>
        <div className="max-h-60 overflow-y-auto rounded-lg border border-gray-200 bg-gray-50">
          {reviews.map((cr) => (
            <div key={cr.id} className="flex items-center justify-between border-b border-gray-100 px-3 py-2 last:border-0">
              <span className="text-sm text-gray-800">{cr.employee_name}</span>
              {cr.department_name && <span className="text-xs text-gray-400">{cr.department_name}</span>}
            </div>
          ))}
        </div>

        {submitting && (
          <div className="mt-3">
            <div className="h-1.5 w-full rounded-full bg-gray-100 overflow-hidden">
              <div className="h-full bg-green-500 transition-all" style={{ width: `${(progress / reviews.length) * 100}%` }} />
            </div>
            <p className="text-[11px] text-gray-400 mt-1">{progress} / {reviews.length} triggered</p>
          </div>
        )}

        <div className="mt-4 flex gap-2 justify-end">
          <button type="button" onClick={onClose} disabled={submitting} className="rounded-lg border border-gray-300 px-4 py-2 text-sm disabled:opacity-50">Cancel</button>
          <button type="button" onClick={() => void handleSubmit()} disabled={submitting} className="rounded-lg bg-green-600 px-4 py-2 text-sm font-semibold text-white hover:bg-green-700 disabled:opacity-50">
            {submitting ? `Triggering... (${progress}/${reviews.length})` : "Trigger All"}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ---------- Bulk Dates Modal ---------- */

function BulkDatesModal({
  reviewIds,
  onClose,
  onSaved,
}: {
  reviewIds: number[];
  onClose: () => void;
  onSaved: () => void;
}) {
  const [scheduledDate, setScheduledDate] = useState("");
  const [selfDeadline, setSelfDeadline] = useState("");
  const [colleagueDeadline, setColleagueDeadline] = useState("");
  const [managerDeadline, setManagerDeadline] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  const handleSubmit = async () => {
    if (!scheduledDate && !selfDeadline && !colleagueDeadline && !managerDeadline) {
      setError("Set at least one date.");
      return;
    }
    setSubmitting(true);
    setError("");
    for (const id of reviewIds) {
      try {
        const body: Record<string, string> = {};
        if (scheduledDate) body.scheduled_start_date = scheduledDate;
        if (selfDeadline) body.self_deadline = selfDeadline;
        if (colleagueDeadline) body.colleague_deadline = colleagueDeadline;
        if (managerDeadline) body.manager_deadline = managerDeadline;
        await api.put(`/api/reviews/${id}/update-deadlines`, body);
      } catch { /* continue */ }
    }
    setSubmitting(false);
    onSaved();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" onClick={onClose}>
      <div className="mx-4 w-full max-w-md rounded-xl bg-white p-6 shadow-xl" onClick={(e) => e.stopPropagation()}>
        <h3 className="text-sm font-semibold text-gray-900 mb-1">Set Dates for {reviewIds.length} Review{reviewIds.length !== 1 ? "s" : ""}</h3>
        <p className="text-xs text-gray-500 mb-4">Leave fields blank to keep current values.</p>
        {error && <p className="mb-3 rounded-lg bg-red-50 px-3 py-2 text-sm text-red-600">{error}</p>}

        <div className="space-y-3">
          <label className="block text-xs font-medium text-gray-700">
            Scheduled Start Date
            <input type="date" value={scheduledDate} onChange={(e) => setScheduledDate(e.target.value)} className="input mt-1 w-full" />
          </label>
          <label className="block text-xs font-medium text-gray-700">
            Self-Assessment Deadline
            <input type="date" value={selfDeadline} onChange={(e) => setSelfDeadline(e.target.value)} className="input mt-1 w-full" />
          </label>
          <label className="block text-xs font-medium text-gray-700">
            Colleague Feedback Deadline
            <input type="date" value={colleagueDeadline} onChange={(e) => setColleagueDeadline(e.target.value)} className="input mt-1 w-full" />
          </label>
          <label className="block text-xs font-medium text-gray-700">
            Manager Review Deadline
            <input type="date" value={managerDeadline} onChange={(e) => setManagerDeadline(e.target.value)} className="input mt-1 w-full" />
          </label>
        </div>

        <div className="mt-5 flex gap-2 justify-end">
          <button type="button" onClick={onClose} className="rounded-lg border border-gray-300 px-4 py-2 text-sm">Cancel</button>
          <button type="button" onClick={() => void handleSubmit()} disabled={submitting} className="rounded-lg bg-pme-blue px-4 py-2 text-sm font-semibold text-white hover:bg-pme-blue/90 disabled:opacity-50">
            {submitting ? "Saving..." : "Apply Dates"}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ---------- How reviews work (collapsible explainer) ---------- */

function HowReviewsWork() {
  const [open, setOpen] = useState(false);
  const steps: [string, string][] = [
    ["1 · Self-assessment", "The employee writes up their own year — wins, challenges, aspirations."],
    ["2 · Colleague feedback", "Chosen colleagues answer a short questionnaire. Optional — it can be skipped."],
    ["3 · Manager review", "The manager writes their review, drawing on the self-assessment and colleague feedback."],
    ["4 · Review meeting", "Manager and employee meet to discuss it, agree a rating, then the review is marked complete."],
    ["5 · Done", "The employee can read the released feedback and add their response."],
  ];
  return (
    <div className="rounded-xl border border-blue-100 bg-blue-50/50">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex w-full items-center gap-2 px-4 py-2.5 text-left text-xs font-semibold text-pme-navy"
      >
        <Info size={14} strokeWidth={1.75} className="text-pme-blue" aria-hidden />
        How an appraisal works
        {open ? <ChevronUp size={14} strokeWidth={2} className="ml-auto text-gray-400" aria-hidden /> : <ChevronDown size={14} strokeWidth={2} className="ml-auto text-gray-400" aria-hidden />}
      </button>
      {open && (
        <div className="grid grid-cols-1 gap-2 px-4 pb-3 sm:grid-cols-5">
          {steps.map(([title, body]) => (
            <div key={title} className="rounded-lg bg-white p-2.5 text-xs shadow-sm">
              <p className="font-semibold text-pme-navy">{title}</p>
              <p className="mt-0.5 text-gray-500">{body}</p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/* ---------- Needs attention strip ---------- */

const _TODAY_ISO = new Date().toISOString().slice(0, 10);

function _relevantDeadline(cr: CycleReview): string | null {
  const s = cr.status;
  if (["not_started", "self_assessment_in_progress", "self_assessment_submitted"].includes(s)) {
    return cr.individual_self_deadline ?? cr.self_assessment_deadline ?? null;
  }
  if (s === "colleague_feedback_in_progress") {
    return cr.individual_colleague_deadline ?? cr.colleague_feedback_deadline ?? null;
  }
  return cr.individual_manager_deadline ?? cr.manager_review_deadline ?? null;
}

function reviewIsOverdue(cr: CycleReview): boolean {
  if (cr.status === "completed" || cr.status === "cancelled" || !cr.triggered_at) return false;
  const dl = _relevantDeadline(cr);
  return !!dl && dl < _TODAY_ISO;
}

function NeedsAttentionStrip({
  reviews,
  colleagueStatus,
  onFilter,
}: {
  reviews: CycleReview[];
  colleagueStatus: Record<string, ColleagueStatus>;
  onFilter: (f: "all" | "active" | "meeting" | "completed") => void;
}) {
  const live = reviews.filter((cr) => !!cr.triggered_at && !cr.is_historical);
  const waitingSelf = live.filter((cr) => ["not_started", "self_assessment_in_progress"].includes(cr.status)).length;
  const waitingPeers = live.filter((cr) => cr.status === "colleague_feedback_in_progress").length;
  const chooseColleagues = live.filter((cr) => cr.status === "self_assessment_submitted").length;
  const readyManager = live.filter((cr) => ["colleague_feedback_complete", "manager_review_in_progress"].includes(cr.status)).length;
  const readyMeeting = live.filter((cr) => ["manager_review_submitted", "meeting_pending", "employee_response_pending"].includes(cr.status)).length;
  const overdue = live.filter(reviewIsOverdue).length;
  const peersBlocking = live.reduce((acc, cr) => {
    if (cr.id == null) return acc;
    const st = colleagueStatus[String(cr.id)];
    if (!st) return acc;
    return acc + st.pending.filter((p) => !p.expired).length;
  }, 0);

  const chips: { label: string; count: number; tone: string; filter?: "all" | "active" | "meeting" | "completed" }[] = [
    { label: `waiting on ${waitingSelf === 1 ? "a self-assessment" : "self-assessments"}`, count: waitingSelf, tone: "border-blue-200 bg-blue-50 text-blue-700", filter: "active" },
    { label: `${chooseColleagues === 1 ? "needs" : "need"} colleagues choosing`, count: chooseColleagues, tone: "border-indigo-200 bg-indigo-50 text-indigo-700", filter: "active" },
    { label: "waiting on colleague feedback", count: waitingPeers, tone: "border-indigo-200 bg-indigo-50 text-indigo-700", filter: "active" },
    { label: "ready for manager review", count: readyManager, tone: "border-amber-200 bg-amber-50 text-amber-800", filter: "active" },
    { label: "ready for review meeting", count: readyMeeting, tone: "border-purple-200 bg-purple-50 text-purple-700", filter: "meeting" },
    { label: "overdue", count: overdue, tone: "border-red-200 bg-red-50 text-red-700", filter: "active" },
  ];
  const visible = chips.filter((c) => c.count > 0);
  if (visible.length === 0 && peersBlocking === 0) return null;

  return (
    <div className="rounded-xl border border-gray-200 bg-white p-3 shadow-sm">
      <p className="text-xs font-semibold text-pme-navy">Where things stand</p>
      <div className="mt-1.5 flex flex-wrap gap-1.5">
        {visible.map((c) => (
          <button
            key={c.label}
            type="button"
            onClick={() => c.filter && onFilter(c.filter)}
            className={`rounded-full border px-2.5 py-1 text-[11px] font-medium ${c.tone}`}
          >
            <span className="font-semibold">{c.count}</span> {c.label}
          </button>
        ))}
        {peersBlocking > 0 && (
          <span className="rounded-full border border-gray-200 bg-gray-50 px-2.5 py-1 text-[11px] font-medium text-gray-600">
            <span className="font-semibold">{peersBlocking}</span> colleague {peersBlocking === 1 ? "response" : "responses"} outstanding
          </span>
        )}
      </div>
    </div>
  );
}

/* ---------- Tab Button ---------- */

function TabBtn({ active, onClick, children }: { active: boolean; onClick: () => void; children: React.ReactNode }) {
  return <button type="button" onClick={onClick} className={["shrink-0 whitespace-nowrap px-4 py-2 text-sm font-semibold", active ? "border-b-2 border-pme-blue text-pme-blue" : "text-gray-500"].join(" ")}>{children}</button>;
}
